/**
 * POSProposals - Peace of Sleep Proposal Management
 *
 * List all POS proposals with view/edit/copy-link action icons.
 * Uses pos-proposal-manage edge function for all operations.
 * ItemSelector replaces fixed bed/topper fields.
 * Create goes straight to live (no draft→send step).
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { formatDate as sharedFormatDate } from '../../../utils/formatters';
import { getCategoryOptions, getAudiences, getSnippet } from '../../../config/proposal-snippets';
import { useAuth } from '../../../contexts/AuthContext';
import { useConfig } from '../../../contexts/ConfigContext';
import { usePagePermission } from '../../../hooks/usePagePermission';
import AccessDenied from '../../../components/AccessDenied';
import ErrorDisplay from '../../../components/ErrorDisplay';
import DataTable, { StatusBadge, type Filter } from '../../../components/DataTable';
import SearchSelect from '../../../components/SearchSelect';
import ImageUpload from './pos-properties/ImageUpload';
import { POS_GOLD } from './pos-properties/constants';
import ItemSelector, { type SelectedItem } from '../components/ItemSelector';
import ProposalSendModal from './pos-proposals/ProposalSendModal';
import ProposalAssignModal from './pos-proposals/ProposalAssignModal';
import ProposalFollowUpModal from './pos-proposals/ProposalFollowUpModal';
import ProposalsBoardView from './pos-proposals/ProposalsBoardView';
import {
  computeProposalTemperature,
  proposalAgeDays,
  PROPOSAL_TEMPERATURE_CONFIG,
} from './pos-proposals/ProposalsBoardConfig';
import ProposalActivityTab from './pos-proposals/ProposalActivityTab';
import ProposalMoveTo from './pos-proposals/ProposalMoveTo';
import { PdfModal } from '../../../components/PdfModal';
import { signPosDocument } from '../../../lib/posDocuments';
import { hasUnlocked } from '../../../lib/training';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

// Proposal rows carry a wide, partly-dynamic shape: the persisted proposal,
// joined fields (pos_group_proposals), and board metadata bolted on by the list
// builder (_isGroup/_members/_group). Fields that aren't always present are
// optional; the index signature covers genuinely dynamic reads (proposal_data,
// nested scope/personalisation/ambassador blobs, version/event rows).
interface ProposalRow {
  id: string;
  reference_code: string;
  /** Secret code in the client's link (26 Oct 2026). */
  access_key?: string | null;
  status: string;
  property_name: string;
  property_city: string | null;
  property_id: string | null;
  property_count: number;
  onboarded_count: number;
  contact_name: string;
  contact_email: string;
  ambassador_name: string | null;
  assigned_ambassador_name: string | null;
  assigned_manager_id: string | null;
  sender_user_id: string | null;
  selected_tier: string;
  annual_total: number;
  created_at: string;
  group_name: string;
  group_proposal_id: string | null;
  is_locked: boolean;
  is_system_lead: boolean;
  signed_pdf_url: string;
  pos_group_proposals: { group_name?: string } | null;
  // Board metadata, only present on the synthesised group rows in listData.
  _isGroup?: boolean;
  _members?: ProposalRow[];
  _group?: ProposalRow;
  // Genuinely dynamic reads (proposal_data and its nested blobs, joined fields).
  [key: string]: unknown;
}
type Row = ProposalRow;

// Nested proposal_data blob (read back from the edge function as JSON).
interface ProposalDataScope {
  rooms?: ScopeRoom[];
  items?: ScopeItem[];
  notes?: string[];
  total_items?: number;
}
interface ProposalData {
  scope?: ProposalDataScope;
  personalisation?: Record<string, unknown>;
  ambassador?: Record<string, unknown>;
  excludes_travel?: boolean;
}

// Detail records returned by the get/preview endpoints and held in the view
// modals. Broader than the list Row: the detail panels read many display-only
// fields (counts, timestamps, pricing) that the list never shows.
interface ProposalDetail {
  id: string;
  reference_code: string;
  /** Secret code in the client's link (26 Oct 2026). */
  access_key?: string | null;
  status: string;
  property_name?: string;
  property_type?: string;
  property_city?: string | null;
  property_province?: string | null;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  ambassador_name?: string | null;
  sender_name?: string | null;
  assigned_ambassador_name?: string | null;
  assigned_manager_name?: string | null;
  selected_tier: string;
  personal_note?: string | null;
  is_locked?: boolean;
  signed_pdf_url?: string;
  visits_per_year: number;
  total_per_visit: number;
  annual_total: number;
  beds_small: number;
  beds_large: number;
  toppers_small: number;
  toppers_large: number;
  created_at?: string;
  sent_at?: string | null;
  viewed_at?: string | null;
  accepted_at?: string | null;
  expires_at?: string | null;
  proposal_data?: ProposalData | null;
}

interface GroupDetail {
  id: string;
  reference_code: string;
  /** Secret code in the client's link (26 Oct 2026). */
  access_key?: string | null;
  status: string;
  group_name?: string;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  ambassador_name?: string | null;
  assigned_ambassador_name?: string | null;
  selected_tier: string;
  personal_note?: string | null;
  is_locked?: boolean;
  signed_pdf_url?: string;
  agreement_pdf_url?: string;
  visits_per_year: number;
  total_per_visit: number;
  annual_total: number;
  volume_discount_pct?: number | null;
  view_count?: number;
  created_at?: string;
  sent_at?: string | null;
  viewed_at?: string | null;
  accepted_at?: string | null;
  expires_at?: string | null;
  locked_at?: string | null;
}

// Analytics event row (pos_proposal_events) as rendered in the activity panel.
interface ProposalEventRow {
  event_type: string;
  event_data?: { seconds?: number; section?: string; is_mobile?: boolean; target?: string } | null;
  created_at: string;
}

// Version-history row rendered in the view modal.
interface ProposalVersionRow {
  version?: number;
  is_final?: boolean;
  change_reason?: string | null;
  user_name?: string | null;
  changed_at?: string;
}

// Onboard preview payloads (group and single-property variants).
interface OnboardPreviewProperty {
  proposal_id: string;
  property_name?: string;
  property_type?: string;
  city?: string | null;
  already_onboarded?: boolean;
  customer_suggestions?: Array<{ id: string; customer_name: string; company_name?: string | null; email?: string | null; match_source: string }>;
  rooms: Array<{ quantity: number }>;
}
interface GroupOnboardPreview {
  group: { id: string; group_name?: string; tier: string; annual_total: number };
  managers: OnboardManager[];
  properties: OnboardPreviewProperty[];
}
interface PropertyOnboardPreviewData {
  proposal: {
    id: string;
    property_name?: string;
    property_type?: string;
    city?: string | null;
    province?: string | null;
    contact_name?: string;
    tier: string;
    annual_total: number;
    rooms?: Array<{ quantity: number }>;
    customer_suggestions?: Array<{ id: string; customer_name: string; company_name?: string | null; email?: string | null; match_source: string }>;
  };
  managers?: OnboardManager[];
}

// Narrow a thrown value to a message without assuming it is an Error.
function errMsg(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

interface RoomEntry {
  name: string;
  quantity: number | string;
  items: SelectedItem[];
}

// Loose shapes read back out of proposal_data.scope. These come from the edge
// function as JSON, so fields are optional and snake_case (unlike the form's
// SelectedItem/RoomEntry which use the picker's camelCase shape).
interface ScopeItem {
  item_id?: string | null;
  item_name?: string;
  quantity?: number;
  unit_price?: number;
  discount?: number;
  discount_type?: string;
}
interface ScopeRoom {
  name?: string;
  quantity?: number;
  items?: ScopeItem[];
  beds_small?: number;
  beds_large?: number;
}

// Shapes built for the edge-function payload (snake_case, server-facing).
interface RoomItemPayload {
  item_id: string | null;
  item_name: string;
  quantity: number;
  unit_price: number;
  discount: number;
  discount_type: string;
}
interface RoomPayload {
  name: string;
  quantity: number;
  items: RoomItemPayload[];
}

// Manager option as returned by the onboard preview endpoint.
interface OnboardManager {
  id: string;
  first_name: string;
  last_name: string;
}

interface FormState {
  property_name: string;
  property_type: string;
  property_city: string;
  property_province: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  selected_tier: string;
  // Category/audience pickers. Optional so the prefill literals that omit them
  // still satisfy FormState; INITIAL_FORM always seeds proposal_category.
  proposal_category?: string;
  proposal_audience?: string;
  // Lifecycle status, only present on an edit-loaded form (handleOpenEdit).
  status?: string;
  personal_note: string;
  intro_paragraph: string;
  scope_notes: string;
  ambassador_photo_url: string;
  ambassador_name: string;
  ambassador_email: string;
  ambassador_phone: string;
  ambassador_title: string;
  ambassador_quote: string;
  hero_image_url: string;
  logo_url: string;
  items: SelectedItem[];
  use_rooms: boolean;
  rooms: RoomEntry[];
  // Property outside our service area: shows a note that travel and
  // accommodation are excluded and quoted per property. Display only, the
  // quoted price never changes.
  excludes_travel: boolean;
  // Empty string = use auto-calculated tier percent. A numeric string (0-100) =
  // admin/territory override. Only editable by admins and territory managers.
  volume_discount_pct_override: string;
  // Empty string = no Rand override. A numeric string = fixed per-visit Rand
  // amount (ex-VAT), an alternative to the percentage. The two are mutually
  // exclusive; the Rand amount takes precedence.
  volume_discount_amt_override: string;
  // Attribution. POS Ambassador credited with sourcing this deal (carried to the
  // property on conversion to drive commission); distinct from the Service
  // Ambassador display block above. Manager is the optional assigned manager.
  // Empty string = none.
  assigned_ambassador_id: string;
  assigned_manager_id: string;
  [key: string]: unknown;
}

interface GroupFormState {
  group_name: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  selected_tier: string;
  personal_note: string;
  intro_paragraph: string;
  scope_notes: string;
  ambassador_photo_url: string;
  ambassador_name: string;
  ambassador_email: string;
  ambassador_phone: string;
  ambassador_title: string;
  ambassador_quote: string;
  hero_image_url: string;
  logo_url: string;
  // See FormState.excludes_travel. Applies to the whole group proposal.
  excludes_travel: boolean;
  // Appointed POS Ambassador who closed the group; applies to every property.
  // Optional so the existing group-form literals don't all need updating.
  assigned_ambassador_id?: string;
  [key: string]: unknown;
}

const formatDate = (dateStr: string | null | undefined) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return sharedFormatDate(d);
};

const STATUS_CONFIG: Record<string, { label: string; bg: string; color: string; icon: string }> = {
  draft: { label: 'Draft', bg: 'var(--color-bg)', color: 'var(--color-text-light)', icon: '📝' },
  pending_approval: { label: 'Pending Approval', bg: 'var(--color-warning-bg)', color: 'var(--color-warning)', icon: '⏳' },
  rejected: { label: 'Rejected', bg: 'var(--color-error-bg, #fee2e2)', color: 'var(--color-error)', icon: '❌' },
  published: { label: 'Sent', bg: 'var(--color-info-bg)', color: 'var(--color-info)', icon: '📤' },
  viewed: { label: 'Viewed', bg: 'rgba(189,152,47,0.12)', color: POS_GOLD, icon: '👁️' },
  accepted: { label: 'Accepted', bg: 'var(--color-success-bg, #dcfce7)', color: 'var(--color-success, #16a34a)', icon: '✅' },
  expired: { label: 'Expired', bg: 'var(--color-error-bg, #fee2e2)', color: 'var(--color-error)', icon: '⏰' },
};

const TIER_LABELS: Record<string, string> = {
  assured: 'Assured',
  distinguished: 'Distinguished',
  signature: 'Signature',
};

const STATUS_FILTER = {
  key: 'status',
  label: 'Status',
  options: [
    { value: 'all', label: 'All Statuses' },
    { value: 'draft', label: 'Draft' },
    { value: 'pending_approval', label: 'Pending Approval' },
    { value: 'published', label: 'Sent' },
    { value: 'viewed', label: 'Viewed' },
    { value: 'accepted', label: 'Accepted' },
    { value: 'expired', label: 'Expired' },
  ],
};

const PROPERTY_TYPES = [
  // Hospitality
  'Hotel',
  'Boutique Hotel',
  'Guest House',
  'B&B',
  'Lodge',
  'Safari Lodge',
  'Resort',
  'Spa Hotel',
  'Airbnb',
  'Serviced Apartments',
  'Holiday Rental',
  // Community Living
  'School Boarding House',
  'University Residence',
  'Student Accommodation',
  'Sports Academy',
  'Military Barracks',
  'Worker Accommodation',
  'Care Facility',
  'Retirement Community',
  'Hostel',
  // General
  'Other',
];

// ----------------------------------------------------------------------------
// Draft persistence — survives screen sleep, tab reload, iOS PWA kill. Used on
// the single-property proposal modal to stop users losing 20 minutes of work.
// Cleared on successful save or explicit discard.
// ----------------------------------------------------------------------------
const DRAFT_VERSION = 1;
const draftKey = (id: string | null) => `posProposalDraft:v${DRAFT_VERSION}:${id ?? 'new'}`;

function readDraft(id: string | null): FormState | null {
  try {
    const raw = localStorage.getItem(draftKey(id));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.form ?? null;
  } catch { return null; }
}

function writeDraft(id: string | null, form: FormState) {
  try {
    localStorage.setItem(draftKey(id), JSON.stringify({ savedAt: Date.now(), form }));
  } catch { /* quota or disabled — silent */ }
}

function clearDraft(id: string | null) {
  try { localStorage.removeItem(draftKey(id)); } catch { /* silent */ }
}

// Group-proposal draft persistence — keyed separately from single-proposal
// drafts. Stores groupForm + groupProperties together so a full restore
// shows both the ambassador/contact fields and the list of attached
// properties the user had built up before the screen slept.
const groupDraftKey = (id: string | null) => `posGroupProposalDraft:v${DRAFT_VERSION}:${id ?? 'new'}`;

function readGroupDraft(id: string | null): { groupForm: GroupFormState; groupProperties: ProposalDetail[] } | null {
  try {
    const raw = localStorage.getItem(groupDraftKey(id));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.groupForm) return null;
    return { groupForm: parsed.groupForm, groupProperties: parsed.groupProperties || [] };
  } catch { return null; }
}

function writeGroupDraft(id: string | null, groupForm: GroupFormState, groupProperties: ProposalDetail[]) {
  try {
    localStorage.setItem(groupDraftKey(id), JSON.stringify({ savedAt: Date.now(), groupForm, groupProperties }));
  } catch { /* silent */ }
}

function clearGroupDraft(id: string | null) {
  try { localStorage.removeItem(groupDraftKey(id)); } catch { /* silent */ }
}

const INITIAL_FORM: FormState = {
  property_name: '',
  property_type: '',
  property_city: '',
  property_province: '',
  contact_name: '',
  contact_email: '',
  contact_phone: '',
  selected_tier: 'assured',
  proposal_category: 'hospitality',
  proposal_audience: '',
  personal_note: '',
  intro_paragraph: '',
  scope_notes: '',
  ambassador_photo_url: '',
  ambassador_name: '',
  ambassador_email: '',
  ambassador_phone: '',
  ambassador_title: 'Peace of Sleep™ Service Ambassador',
  ambassador_quote: 'If you have any questions about this proposal or about Peace of Sleep™, I\'m here to help. I look forward to hearing from you.',
  hero_image_url: '',
  logo_url: '',
  items: [],
  use_rooms: false,
  rooms: [],
  excludes_travel: false,
  volume_discount_pct_override: '',
  volume_discount_amt_override: '',
  assigned_ambassador_id: '',
  assigned_manager_id: '',
};

function POSProposals() {
  const navigate = useNavigate();
  const location = useLocation();
  const { supabase, user, hasPermission, isSuperAdmin } = useAuth();
  // Set when the create modal was opened by "Build Proposal" from a POS Lead, so
  // we can link the new proposal back to that lead on save.
  const pendingLeadIdRef = useRef<string | null>(null);
  const { formatCurrency, countryCode } = useConfig();
  const { canAccess, isLoading: permissionLoading } = usePagePermission('pos_proposals');

  // Ambassador mode: own-scope user with ambassador portal access
  const permsRaw = user?.permissions as Record<string, unknown> | undefined;
  const isAmbassador = permsRaw?._scope === 'own' &&
    (permsRaw?.ambassador_portal as Record<string, unknown> | undefined)?.access === true;

  // Volume discount override is restricted to Country Admins and Territory Managers.
  // POS Ambassadors and everyone else always get the auto-calculated tier percent.
  const userScope = permsRaw?._scope;
  const canOverrideDiscount = userScope === 'global' || userScope === 'all' || userScope === 'territory';

  // Attribution pickers (POS Ambassador + Manager) are admin housekeeping; the
  // server only honours them from admins, so the section is admin-only here too.
  // The ambassador pick drives commission, so it is additionally gated to payout
  // admins, mirroring the property board's Link-ambassador action.
  const isAdminUser = isSuperAdmin || userScope === 'global' || userScope === 'all';
  const canSetAmbassador = isSuperAdmin || hasPermission('payouts', 'manage');

  // Active users for the attribution dropdowns. Ambassadors = is_ambassador only
  // (same filter as the property board picker); managers = any active user.
  const [ambassadorOptions, setAmbassadorOptions] = useState<{ id: string; name: string }[]>([]);
  const [managerOptions, setManagerOptions] = useState<{ id: string; name: string }[]>([]);
  useEffect(() => {
    if (!isAdminUser) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('users')
        .select('id, first_name, last_name, email, is_ambassador')
        .eq('status', 'active')
        .order('first_name');
      if (cancelled || !data) return;
      const opts = data.map((u: Record<string, unknown>) => ({
        id: u.id as string,
        name: `${(u.first_name as string) || ''} ${(u.last_name as string) || ''}`.trim() || (u.email as string) || '—',
        is_ambassador: u.is_ambassador === true,
      }));
      setManagerOptions(opts.map(({ id, name }) => ({ id, name })));
      setAmbassadorOptions(opts.filter((o) => o.is_ambassador).map(({ id, name }) => ({ id, name })));
    })();
    return () => { cancelled = true; };
  }, [supabase, isAdminUser]);

  const [listTab, setListTab] = useState('hospitality');
  const [viewMode, setViewMode] = useState<'list' | 'board' | 'metrics'>(() => {
    const saved = localStorage.getItem('posProposalsViewMode');
    if (saved === 'board') return 'board';
    if (saved === 'metrics') return 'metrics';
    return 'list';
  });
  useEffect(() => {
    localStorage.setItem('posProposalsViewMode', viewMode);
  }, [viewMode]);

  // Metrics view: date range + click-to-filter handoff to list view.
  // metricsCreatorFilter pre-populates the List view's "Created By" filter
  // when the admin clicks a row in Metrics. Cleared by changing it or by
  // the user clearing the filter inside the list.
  const [metricsRange, setMetricsRange] = useState<'30d' | '90d' | 'all'>('all');
  const [metricsCreatorFilter, setMetricsCreatorFilter] = useState<string>('');

  // Board view: free-text search + dropdown filters. Mirror of the
  // ServiceLogs board pattern. None of these affect the list view.
  const [boardSearch, setBoardSearch] = useState('');
  const [boardTierFilter, setBoardTierFilter] = useState('');
  const [boardAmbassadorFilter, setBoardAmbassadorFilter] = useState('');

  // Per-column sort, persisted to localStorage so the operator's choice
  // survives a refresh. Default 'date_desc' (Newest) per column.
  const [boardColumnSorts, setBoardColumnSorts] = useState<Record<string, string>>(() => {
    try {
      const saved = localStorage.getItem('posProposalsBoardSorts');
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });
  useEffect(() => {
    localStorage.setItem('posProposalsBoardSorts', JSON.stringify(boardColumnSorts));
  }, [boardColumnSorts]);
  const setBoardColumnSort = useCallback((colId: string, sortType: string) => {
    setBoardColumnSorts(prev => ({ ...prev, [colId]: sortType }));
  }, []);
  const [proposals, setProposals] = useState<Row[]>([]);
  // Map<proposal_id, latest ambassador-action event> for the board card preview.
  // Loaded lazily when board view is active so the list view never pays the cost.
  const [latestEvents, setLatestEvents] = useState<Map<string, Row>>(new Map());
  // Map<proposal_id, when each kind of follow-up last went out>, for the list's
  // Followed up column and filter, so the sender can see who has had one.
  const [followUps, setFollowUps] = useState<Map<string, { emailAt?: string; whatsAppAt?: string }>>(new Map());
  const [pdfPreview, setPdfPreview] = useState<{ url: string; title: string } | null>(null);
  // Signed agreements are stored as a path in the private pos-documents bucket.
  const openSignedAgreement = async (path: string, title: string) => {
    const url = await signPosDocument(supabase, path);
    if (!url) {
      alert('Could not open the agreement PDF.');
      return;
    }
    setPdfPreview({ url, title });
  };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null);

  // Training unlock: only relevant for ambassadors. Non-ambassadors always pass.
  const [submitUnlocked, setSubmitUnlocked] = useState<boolean>(!isAmbassador);
  useEffect(() => {
    if (!isAmbassador || !user?.id) { setSubmitUnlocked(!isAmbassador); return; }
    hasUnlocked(supabase, user.id, 'ambassador_submit_proposal').then(setSubmitUnlocked);
  }, [isAmbassador, user?.id, supabase]);

  // Group state
  const [groupProposals, setGroupProposals] = useState<Row[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [groupModalLoading, setGroupModalLoading] = useState(false);
  const [groupModalError, setGroupModalError] = useState<string | null>(null);
  const [groupForm, setGroupForm] = useState<GroupFormState>({
    group_name: '', contact_name: '', contact_email: '', contact_phone: '', selected_tier: 'assured',
    personal_note: '', intro_paragraph: '', scope_notes: '',
    ambassador_photo_url: '', ambassador_name: '', ambassador_email: '', ambassador_phone: '',
    ambassador_title: 'Peace of Sleep™ Service Ambassador',
    ambassador_quote: 'If you have any questions about this proposal or about Peace of Sleep™, I\'m here to help. I look forward to hearing from you.',
    hero_image_url: '', logo_url: '', excludes_travel: false,
  });
  const [groupProperties, setGroupProperties] = useState<ProposalDetail[]>([]);
  // Member property names to list when a group was started from a Leads collection.
  const [groupPrefillMembers, setGroupPrefillMembers] = useState<string[]>([]);
  const [groupUploadId, setGroupUploadId] = useState('');
  // Sub-modal: add/edit property in group
  const [showPropertySubModal, setShowPropertySubModal] = useState(false);
  const [editingPropertyId, setEditingPropertyId] = useState<string | null>(null); // null = add new, string = edit existing
  const [propertySubForm, setPropertySubForm] = useState<FormState>({ ...INITIAL_FORM });
  const [propertySubLoading, setPropertySubLoading] = useState(false);
  const [propertySubError, setPropertySubError] = useState<string | null>(null);
  // Link existing modal
  const [showLinkPicker, setShowLinkPicker] = useState(false);
  const [linkPickerSearch, setLinkPickerSearch] = useState('');
  // Inline group rows in the proposal list: which groups are expanded, and a
  // mirror of the table's search query so a member match opens its group.
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [listSearch, setListSearch] = useState('');
  // View group modal
  const [viewGroup, setViewGroup] = useState<GroupDetail | null>(null);
  const [viewGroupProperties, setViewGroupProperties] = useState<ProposalDetail[]>([]);
  // Onboard modal
  const [onboardPreview, setOnboardPreview] = useState<GroupOnboardPreview | null>(null);
  const [onboardManagers, setOnboardManagers] = useState<Record<string, string>>({});
  type CustomerChoice = { mode: 'existing'; customer_id: string } | { mode: 'create' };
  const [onboardCustomers, setOnboardCustomers] = useState<Record<string, CustomerChoice>>({});
  const [onboardLoading, setOnboardLoading] = useState(false);

  // Shared create/edit modal
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null); // null = create, string = edit
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  // Draft persistence — shown when the open handler rehydrates unsaved form state
  const [draftRestored, setDraftRestored] = useState(false);
  const [groupDraftRestored, setGroupDraftRestored] = useState(false);

  // View modal
  const [viewProposal, setViewProposal] = useState<ProposalDetail | null>(null);
  const [viewProposalTab, setViewProposalTab] = useState<'overview' | 'activity'>('overview');
  const [proposalEvents, setProposalEvents] = useState<ProposalEventRow[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [proposalVersions, setProposalVersions] = useState<ProposalVersionRow[]>([]);

  // Send modal
  const [sendingProposal, setSendingProposal] = useState<Row | null>(null);
  // Follow-up modal, the branded nudge the platform sends itself
  const [followUpProposal, setFollowUpProposal] = useState<Row | null>(null);
  const [assigningProposal, setAssigningProposal] = useState<Row | null>(null);

  // Form state
  const [form, setForm] = useState<FormState>({ ...INITIAL_FORM });

  // Unique ID for storage paths (generated once per modal open)
  const [uploadId, setUploadId] = useState('');

  const callEdgeFunction = useCallback(async (action: string, body: Record<string, unknown> = {}) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Session expired');

    const res = await fetch(`${SUPABASE_URL}/functions/v1/pos-proposal-manage`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ action, ...body }),
    });

    const data = await res.json();
    if (!res.ok || !data.success) {
      throw new Error(data.error || data.message || 'Request failed');
    }
    return data;
  }, [supabase]);

  // === GROUP FUNCTIONS ===
  const callGroupFunction = useCallback(async (action: string, body: Record<string, unknown> = {}) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Session expired');
    const res = await fetch(`${SUPABASE_URL}/functions/v1/pos-group-proposal-manage`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
      body: JSON.stringify({ action, ...body }),
    });
    const data = await res.json();
    if (!res.ok && data.error) throw new Error(data.error);
    return data;
  }, [supabase]);

  const loadGroupProposals = useCallback(async () => {
    try {
      setGroupsLoading(true);
      const data = await callGroupFunction('list');
      setGroupProposals(data.groups || []);
    } catch (_err) {
      console.error('Error loading group proposals:', _err);
    } finally {
      setGroupsLoading(false);
    }
  }, [callGroupFunction]);

  // Load groups on mount (and on tab switches) so the Groups tab badge count
  // is correct before the tab is ever opened. Admin-only; ambassadors never
  // see groups and the list endpoint rejects them.
  useEffect(() => {
    if (canAccess && !isAmbassador) loadGroupProposals();
  }, [canAccess, isAmbassador, listTab, loadGroupProposals]);

  // Auto-save the in-progress proposal form to localStorage while the modal is
  // open. Users were losing 20+ minutes of work when the screen slept or the
  // iOS PWA got killed. Debounced so we only write after typing settles.
  useEffect(() => {
    if (!showModal) return;
    const t = setTimeout(() => writeDraft(editingId, form), 800);
    return () => clearTimeout(t);
  }, [form, showModal, editingId]);

  // Confirm-on-close guard. Only prompts when the user has genuinely put work
  // in (i.e. a draft exists for this key). On confirm, discards the draft and
  // closes; on cancel, keeps the modal open.
  const requestCloseModal = useCallback(() => {
    const hasDraft = !!readDraft(editingId);
    if (hasDraft && !confirm('You have unsaved changes. Discard them and close?')) return;
    clearDraft(editingId);
    setDraftRestored(false);
    setShowModal(false);
  }, [editingId]);

  const discardDraftBanner = useCallback(() => {
    clearDraft(editingId);
    setDraftRestored(false);
  }, [editingId]);

  // Same auto-save + close guard for the group proposal modal. Draft stores
  // the group form AND the attached properties list so a full restore
  // returns the user exactly where they left off.
  useEffect(() => {
    if (!showGroupModal) return;
    const t = setTimeout(() => writeGroupDraft(editingGroupId, groupForm, groupProperties), 800);
    return () => clearTimeout(t);
  }, [groupForm, groupProperties, showGroupModal, editingGroupId]);

  const requestCloseGroupModal = useCallback(() => {
    const hasDraft = !!readGroupDraft(editingGroupId);
    if (hasDraft && !confirm('You have unsaved changes. Discard them and close?')) return;
    clearGroupDraft(editingGroupId);
    setGroupDraftRestored(false);
    setShowGroupModal(false);
  }, [editingGroupId]);

  const discardGroupDraftBanner = useCallback(() => {
    clearGroupDraft(editingGroupId);
    setGroupDraftRestored(false);
  }, [editingGroupId]);

  // === GROUP: OPEN CREATE ===
  const handleOpenGroupCreate = () => {
    const userName = [user?.first_name, user?.last_name].filter(Boolean).join(' ') || '';
    const fresh: GroupFormState = {
      group_name: '', contact_name: '', contact_email: '', contact_phone: '', selected_tier: 'assured',
      personal_note: '', intro_paragraph: '', scope_notes: '',
      ambassador_photo_url: '', ambassador_name: userName, ambassador_email: user?.email || '', ambassador_phone: user?.phone || '',
      ambassador_title: 'Peace of Sleep™ Service Ambassador',
      ambassador_quote: 'If you have any questions about this proposal or about Peace of Sleep™, I\'m here to help. I look forward to hearing from you.',
      hero_image_url: '', logo_url: '', excludes_travel: false,
    };
    const draft = readGroupDraft(null);
    if (draft) {
      setGroupForm(draft.groupForm);
      setGroupProperties(draft.groupProperties);
      setGroupDraftRestored(true);
    } else {
      setGroupForm(fresh);
      setGroupProperties([]);
      setGroupDraftRestored(false);
    }
    setEditingGroupId(null);
    setGroupUploadId(Date.now().toString(36));
    setGroupModalError(null);
    setGroupPrefillMembers([]);
    setShowGroupModal(true);
  };

  // === GROUP: OPEN EDIT ===
  const handleOpenGroupEdit = async (row: { id: string }) => {
    try {
      const data = await callGroupFunction('get', { id: row.id });
      const g = data.group;
      const pd = g.proposal_data || {};
      const amb = pd.ambassador || {};
      const pers = pd.personalisation || {};
      setGroupForm({
        group_name: g.group_name || '',
        contact_name: g.contact_name || '',
        contact_email: g.contact_email || '',
        contact_phone: g.contact_phone || '',
        selected_tier: g.selected_tier || 'assured',
        status: g.status || 'draft',
        personal_note: g.personal_note || '',
        intro_paragraph: pers.intro_paragraph || '',
        scope_notes: (pers.scope_notes || []).join?.('\n') || '',
        ambassador_photo_url: pers.ambassador_photo_url || '',
        ambassador_name: amb.name || g.ambassador_name || '',
        ambassador_email: amb.email || '',
        ambassador_phone: amb.phone || '',
        ambassador_title: amb.title || 'Peace of Sleep™ Service Ambassador',
        ambassador_quote: amb.quote || '',
        hero_image_url: g.hero_image_url || pers.hero_image_url || '',
        logo_url: g.logo_url || pers.logo_url || '',
        excludes_travel: pd.excludes_travel === true,
        assigned_ambassador_id: g.assigned_ambassador_id || '',
      });
      setEditingGroupId(g.id);
      setGroupProperties(data.properties || []);
      // Rehydrate any unsaved draft for this group (screen slept mid-edit)
      const draft = readGroupDraft(g.id);
      if (draft) {
        setGroupForm(draft.groupForm);
        setGroupProperties(draft.groupProperties);
        setGroupDraftRestored(true);
      } else {
        setGroupDraftRestored(false);
      }
      setGroupUploadId(g.reference_code || Date.now().toString(36));
      setGroupModalError(null);
      setShowGroupModal(true);
    } catch (err: unknown) {
      alert('Error loading group: ' + errMsg(err));
    }
  };

  // === GROUP: VIEW ===
  const handleViewGroup = async (row: Row) => {
    try {
      const data = await callGroupFunction('get', { id: row.id });
      setViewGroup(data.group);
      setViewGroupProperties(data.properties || []);
      // Load analytics events for group (stored against linked property proposals)
      setEventsLoading(true);
      setProposalEvents([]);
      if (data.properties && data.properties.length > 0) {
        const propIds = data.properties.map((p: { id: string }) => p.id);
        const { data: events } = await supabase
          .from('pos_proposal_events')
          .select('event_type, event_data, created_at')
          .in('proposal_id', propIds)
          .order('created_at', { ascending: false })
          .limit(100);
        setProposalEvents(events || []);
      }
      setEventsLoading(false);
      // Load version history
      try {
        const vData = await callGroupFunction('get_versions', { id: row.id });
        setProposalVersions(vData.versions || []);
      } catch { setProposalVersions([]); }
    } catch (err: unknown) {
      alert('Error loading group: ' + errMsg(err));
    }
  };

  // === GROUP: SAVE (create or update) ===
  const handleGroupSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setGroupModalLoading(true);
      setGroupModalError(null);

      const ambassadorDetails = {
        name: groupForm.ambassador_name, email: groupForm.ambassador_email,
        phone: groupForm.ambassador_phone, title: groupForm.ambassador_title,
        quote: groupForm.ambassador_quote,
      };
      const personalisation = {
        intro_paragraph: groupForm.intro_paragraph || null,
        scope_notes: groupForm.scope_notes ? groupForm.scope_notes.split('\n').filter((n: string) => n.trim()) : null,
        ambassador_photo_url: groupForm.ambassador_photo_url || null,
        hero_image_url: groupForm.hero_image_url || null,
        logo_url: groupForm.logo_url || null,
      };

      if (editingGroupId) {
        await callGroupFunction('update', {
          id: editingGroupId, ...groupForm,
          ambassador_details: ambassadorDetails,
          personalisation,
        });
      } else {
        const result = await callGroupFunction('create', {
          ...groupForm, ambassador_name: groupForm.ambassador_name,
        });
        // Migrate the "new" draft key to the new id so a sleep between
        // create and the first property-add doesn't strand the work.
        const createdDraft = readGroupDraft(null);
        if (createdDraft) {
          writeGroupDraft(result.group.id, createdDraft.groupForm, createdDraft.groupProperties);
          clearGroupDraft(null);
        }
        setEditingGroupId(result.group.id);
        // Store ambassador + personalisation
        await callGroupFunction('update', {
          id: result.group.id,
          ambassador_details: ambassadorDetails,
          personalisation,
          excludes_travel: groupForm.excludes_travel,
        });
      }

      // Clear the draft on successful save — DB now holds the canonical state.
      clearGroupDraft(editingGroupId);
      setGroupDraftRestored(false);

      if (!editingGroupId) {
        // Stay in modal after creation so user can add properties
        setCopyFeedback('Group created! Now add properties.');
        setTimeout(() => setCopyFeedback(null), 3000);
      } else {
        setCopyFeedback('Group updated.');
        setTimeout(() => setCopyFeedback(null), 2000);
      }
      loadGroupProposals();
    } catch (err: unknown) {
      setGroupModalError(errMsg(err));
    } finally {
      setGroupModalLoading(false);
    }
  };

  // === GROUP: DELETE ===
  const handleDeleteGroup = async () => {
    if (!editingGroupId) return;
    if (!confirm('Delete this group proposal? Properties will become standalone.')) return;
    try {
      setGroupModalLoading(true);
      await callGroupFunction('delete', { id: editingGroupId });
      clearGroupDraft(editingGroupId);
      setGroupDraftRestored(false);
      setShowGroupModal(false);
      setCopyFeedback('Group deleted.');
      setTimeout(() => setCopyFeedback(null), 2000);
      loadGroupProposals();
    } catch (err: unknown) {
      setGroupModalError('Delete failed: ' + errMsg(err));
    } finally {
      setGroupModalLoading(false);
    }
  };

  // === GROUP: SEND ===
  const handleSendGroup = async () => {
    if (!editingGroupId || groupProperties.length === 0) return;
    try {
      setGroupModalLoading(true);
      const result = await callGroupFunction('send', { id: editingGroupId });
      clearGroupDraft(editingGroupId);
      setGroupDraftRestored(false);
      setShowGroupModal(false);
      if (result.proposal_url) navigator.clipboard.writeText(result.proposal_url);
      setCopyFeedback('Group proposal sent! Link copied.');
      setTimeout(() => setCopyFeedback(null), 3000);
      loadGroupProposals();
    } catch (err: unknown) {
      setGroupModalError('Send failed: ' + errMsg(err));
    } finally {
      setGroupModalLoading(false);
    }
  };

  // One-click "Mark as sent" from the list, so the draft → sent step does
  // not require opening Share and picking a channel. Groups publish the whole
  // portfolio (group + every child); singles record a manual send (sender
  // snapshot + sent date). The Share modal stays for composing the message.
  const handleQuickMarkSent = async (row: Row) => {
    const isGroup = groupProposals.some(g => g.id === row.id);
    const name = row.property_name || row.group_name || row.reference_code || 'this proposal';
    if (!confirm(
      `Mark ${name} as sent? This records it as sent to the customer and stamps today's date.` +
      (isGroup ? ' Every property in the portfolio is included.' : ''),
    )) return;
    try {
      if (isGroup) {
        await callGroupFunction('send', { id: row.id });
      } else {
        const { data, error } = await supabase.functions.invoke('pos-proposal-manage', {
          body: { action: 'mark_sent', id: row.id, channel: 'manual' },
        });
        if (error) throw error;
        if (data?.error) throw new Error(data.error);
      }
      setCopyFeedback('Marked as sent.');
      setTimeout(() => setCopyFeedback(null), 2500);
      loadProposals();
      loadGroupProposals();
    } catch (err: unknown) {
      alert('Could not mark as sent: ' + errMsg(err));
    }
  };

  // === GROUP: REMOVE PROPERTY ===
  const handleRemoveGroupProperty = async (proposalId: string) => {
    if (!confirm('Remove this property from the group?')) return;
    try {
      await callGroupFunction('remove_property', { proposal_id: proposalId });
      const data = await callGroupFunction('get', { id: editingGroupId });
      setGroupProperties(data.properties || []);
      loadGroupProposals();
      loadProposals(); // refresh standalone list so the removed property is linkable to another group right away
    } catch (err: unknown) {
      alert('Error: ' + errMsg(err));
    }
  };

  // === GROUP: OPEN PROPERTY SUB-MODAL (new) ===
  const handleOpenPropertySubModal = () => {
    setEditingPropertyId(null);
    setPropertySubForm({ ...INITIAL_FORM, items: [] });
    setPropertySubError(null);
    setShowPropertySubModal(true);
  };

  // === GROUP: EDIT PROPERTY IN GROUP ===
  const handleEditGroupProperty = async (proposal: { id: string }) => {
    try {
      const data = await callEdgeFunction('get', { id: proposal.id });
      const p = data.proposal;
      const pd = p.proposal_data || {};
      const scope = pd.scope || {};

      // Same item conversion logic as handleOpenEdit
      const toSelectorItems = (arr: ScopeItem[]): SelectedItem[] => (arr || []).map((it: ScopeItem, idx: number) => ({
        id: `edit-${idx}-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
        catalogItemId: it.item_id as string,
        name: it.item_name as string,
        code: '',
        description: '',
        quantity: it.quantity || 1,
        unitPrice: it.unit_price || 0,
        discount: it.discount || 0,
        discountType: it.discount_type || 'percentage',
        unit: 'qty',
      }));

      const hasRoomItems = Array.isArray(scope.rooms) && scope.rooms.length > 0
        && scope.rooms.some((r: ScopeRoom) => Array.isArray(r.items) && r.items.length > 0);

      let savedItems: SelectedItem[] = [];
      let savedRooms: RoomEntry[] = [];
      let useRooms = false;

      if (hasRoomItems) {
        useRooms = true;
        savedRooms = scope.rooms.map((r: ScopeRoom) => ({
          name: r.name || '',
          quantity: r.quantity || 1,
          items: toSelectorItems(r.items ?? []),
        }));
      } else if (scope.items && scope.items.length > 0) {
        savedItems = toSelectorItems(scope.items);
      } else {
        const fallbackItems = [];
        if (p.beds_small > 0) fallbackItems.push({ item_id: null, item_name: 'Beds: Small', quantity: p.beds_small, unit_price: 350 });
        if (p.beds_large > 0) fallbackItems.push({ item_id: null, item_name: 'Beds: Large', quantity: p.beds_large, unit_price: 450 });
        if (p.toppers_small > 0) fallbackItems.push({ item_id: null, item_name: 'Topper : Small', quantity: p.toppers_small, unit_price: 300 });
        if (p.toppers_large > 0) fallbackItems.push({ item_id: null, item_name: 'Topper : Large', quantity: p.toppers_large, unit_price: 400 });
        savedItems = toSelectorItems(fallbackItems);
      }

      setEditingPropertyId(p.id);
      setPropertySubForm({
        ...INITIAL_FORM,
        property_name: p.property_name || '',
        property_type: p.property_type || '',
        property_city: p.property_city || '',
        property_province: p.property_province || '',
        contact_name: p.contact_name || '',
        contact_email: p.contact_email || '',
        contact_phone: p.contact_phone || '',
        items: savedItems,
        use_rooms: useRooms,
        rooms: savedRooms,
      });
      setPropertySubError(null);
      setShowPropertySubModal(true);
    } catch (err: unknown) {
      alert('Error loading property: ' + errMsg(err));
    }
  };

  // === GROUP: LINK EXISTING PROPERTY ===
  const handleLinkProperty = async (proposalId: string) => {
    if (!editingGroupId) return;
    try {
      await callGroupFunction('link_property', { group_id: editingGroupId, proposal_id: proposalId });
      const data = await callGroupFunction('get', { id: editingGroupId });
      setGroupProperties(data.properties || []);
      setShowLinkPicker(false);
      loadGroupProposals();
      loadProposals(); // Refresh property list to show GRP badge
    } catch (err: unknown) {
      alert('Error: ' + errMsg(err));
    }
  };

  // === GROUP: SAVE PROPERTY (add new or update existing) ===
  const handlePropertySubSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingGroupId) return;
    try {
      setPropertySubLoading(true);
      setPropertySubError(null);

      const payload: Record<string, unknown> = {
        property_name: propertySubForm.property_name,
        property_type: propertySubForm.property_type,
        property_city: propertySubForm.property_city,
        property_province: propertySubForm.property_province,
        contact_name: propertySubForm.contact_name,
        contact_email: propertySubForm.contact_email,
        contact_phone: propertySubForm.contact_phone,
      };

      // Items or rooms
      if (propertySubForm.use_rooms && propertySubForm.rooms.length > 0) {
        const roomsPayload: RoomPayload[] = propertySubForm.rooms.map((r: RoomEntry) => ({
          name: r.name, quantity: parseInt(String(r.quantity)) || 1,
          items: (r.items || []).map((it: SelectedItem) => ({
            item_id: it.catalogItemId, item_name: it.name, quantity: it.quantity,
            unit_price: it.unitPrice, discount: it.discount || 0, discount_type: it.discountType || 'percentage',
          })),
        }));
        payload.rooms = roomsPayload;
        const totalItems = roomsPayload.reduce((s: number, r: RoomPayload) => s + r.items.length, 0);
        if (totalItems < 1) { setPropertySubError('At least 1 item is required.'); return; }
      } else if (propertySubForm.items.length > 0) {
        payload.items = propertySubForm.items.map((it: SelectedItem) => ({
          item_id: it.catalogItemId, item_name: it.name, quantity: it.quantity,
          unit_price: it.unitPrice, discount: it.discount || 0, discount_type: it.discountType || 'percentage',
        }));
      } else {
        setPropertySubError('At least 1 item is required.');
        return;
      }

      if (editingPropertyId) {
        // Update existing property
        await callGroupFunction('update_property', { proposal_id: editingPropertyId, ...payload });
      } else {
        // Add new property
        await callGroupFunction('add_property', { group_id: editingGroupId, ...payload });
      }

      const data = await callGroupFunction('get', { id: editingGroupId });
      setGroupProperties(data.properties || []);
      setShowPropertySubModal(false);
      setEditingPropertyId(null);
      loadGroupProposals();
    } catch (err: unknown) {
      setPropertySubError(errMsg(err));
    } finally {
      setPropertySubLoading(false);
    }
  };

  // === ONBOARD: PREVIEW ===
  const callOnboardFunction = useCallback(async (action: string, body: Record<string, unknown> = {}) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Session expired');
    const res = await fetch(`${SUPABASE_URL}/functions/v1/pos-onboard`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
      body: JSON.stringify({ action, ...body }),
    });
    const data = await res.json();
    if (!res.ok && data.error) throw new Error(data.error);
    return data;
  }, [supabase]);

  const handleOnboardPreview = async (row: Row) => {
    try {
      setOnboardLoading(true);
      const data = await callOnboardFunction('preview', { group_proposal_id: row.id });
      setOnboardPreview(data);
      setOnboardManagers({});
      // Pre-fill customer choice with the top suggestion when one exists,
      // so the common case (the suggestion is right) is a single click to
      // accept. Admin can change to a different existing customer or
      // create new from the dropdown.
      const initialCustomers: Record<string, CustomerChoice> = {};
      type PreviewProperty = { proposal_id: string; customer_suggestions?: Array<{ id?: string }> };
      for (const p of (data?.properties ?? []) as PreviewProperty[]) {
        const top = (p.customer_suggestions ?? [])[0];
        if (top?.id) {
          initialCustomers[p.proposal_id] = { mode: 'existing', customer_id: top.id };
        }
      }
      setOnboardCustomers(initialCustomers);
    } catch (err: unknown) {
      alert('Error: ' + errMsg(err));
    } finally {
      setOnboardLoading(false);
    }
  };

  const handleOnboardConfirm = async () => {
    if (!onboardPreview) return;
    try {
      setOnboardLoading(true);
      await callOnboardFunction('onboard', {
        group_proposal_id: onboardPreview.group.id,
        manager_assignments: onboardManagers,
        customer_assignments: onboardCustomers,
      });
      setOnboardPreview(null);
      setCopyFeedback('Properties onboarded successfully!');
      setTimeout(() => setCopyFeedback(null), 3000);
      loadGroupProposals();
    } catch (err: unknown) {
      alert('Onboard failed: ' + errMsg(err));
    } finally {
      setOnboardLoading(false);
    }
  };

  // === PROPERTY SUB-MODAL ROOM HELPERS ===
  const addSubRoom = () => setPropertySubForm(f => ({ ...f, rooms: [...f.rooms, { name: '', quantity: 1, items: [] }] }));
  const updateSubRoom = (index: number, field: string, value: string | number | SelectedItem[]) => setPropertySubForm(f => ({ ...f, rooms: f.rooms.map((r, i) => i === index ? { ...r, [field]: value } : r) }));
  const removeSubRoom = (index: number) => setPropertySubForm(f => ({ ...f, rooms: f.rooms.filter((_, i: number) => i !== index) }));

  const loadProposals = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await callEdgeFunction('list');
      setProposals(data.proposals || []);
    } catch (err: unknown) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  }, [callEdgeFunction]);

  useEffect(() => {
    if (canAccess) {
      loadProposals();
    }
  }, [canAccess, loadProposals]);

  // Fetch the latest ambassador-action event per visible proposal — only when the
  // board view is active. Powers the "📞 Barry: 'spoke, deciding Friday'" preview
  // line on each card. Customer-side events (view, time_on_page, cta_click) are
  // intentionally excluded — those signals already render in the activity line.
  useEffect(() => {
    if (viewMode !== 'board' || proposals.length === 0) {
      setLatestEvents(new Map());
      return;
    }
    let cancelled = false;
    (async () => {
      const ids = proposals.map((p) => p.id as string).filter(Boolean);
      const { data } = await supabase
        .from('pos_proposal_events')
        .select('proposal_id, event_type, event_data, created_at, created_by, users:created_by(first_name, last_name)')
        .in('proposal_id', ids)
        .in('event_type', [
          'manual_activity',
          'sent_via_email', 'sent_via_whatsapp', 'sent_via_sms',
          'followup_sent', 'followup_whatsapp_sent',
          'submitted_for_approval', 'approved_by_admin', 'rejected_by_admin',
          'manual_stage_change', 'marked_lost', 'marked_accepted_admin', 're_engaged', 'recalled_to_drafting',
        ])
        .order('created_at', { ascending: false })
        .limit(500);
      if (cancelled || !data) return;
      // First-seen per proposal_id wins (already sorted descending = latest first)
      const map = new Map<string, Row>();
      for (const evt of data as unknown as Row[]) {
        const pid = evt.proposal_id as string;
        if (!map.has(pid)) map.set(pid, evt);
      }
      setLatestEvents(map);
    })();
    return () => { cancelled = true; };
  }, [viewMode, proposals, supabase]);

  // Every recorded follow-up, latest per proposal and channel. Read by event type
  // rather than by proposal id, so the request stays short however many
  // proposals are loaded. Paged because there can be more than one page of rows.
  useEffect(() => {
    if (proposals.length === 0) {
      setFollowUps(new Map());
      return;
    }
    let cancelled = false;
    (async () => {
      const PAGE = 1000;
      const rows: Row[] = [];
      for (let from = 0; ; from += PAGE) {
        const { data, error: fuErr } = await supabase
          .from('pos_proposal_events')
          .select('proposal_id, event_type, created_at')
          .in('event_type', ['followup_sent', 'followup_whatsapp_sent'])
          .order('created_at', { ascending: false })
          .range(from, from + PAGE - 1);
        if (fuErr || !data) return;
        rows.push(...(data as unknown as Row[]));
        if (data.length < PAGE) break;
      }
      if (cancelled) return;
      const map = new Map<string, { emailAt?: string; whatsAppAt?: string }>();
      for (const evt of rows) {
        const pid = evt.proposal_id as string;
        const entry = map.get(pid) ?? {};
        const at = evt.created_at as string;
        if (evt.event_type === 'followup_sent' && !entry.emailAt) entry.emailAt = at;
        if (evt.event_type === 'followup_whatsapp_sent' && !entry.whatsAppAt) entry.whatsAppAt = at;
        map.set(pid, entry);
      }
      setFollowUps(map);
    })();
    return () => { cancelled = true; };
  }, [proposals, supabase]);

  // === OPEN PREVIEW (admin-only safe path — view never counts) ===
  // The link's secret code (k), without which the page shows no contact details (26 Oct 2026).
  const keyParam = (accessKey?: string | null) => (accessKey ? `&k=${encodeURIComponent(accessKey)}` : '');

  const handleOpenPreview = (refCode: string, accessKey?: string | null) => {
    const url = `https://peaceofsleep.vip/p/?ref=${refCode}&c=${countryCode.toLowerCase()}&preview=1${keyParam(accessKey)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    setCopyFeedback('Admin preview opened — do not share this link with the customer. Use the Share button to send.');
    setTimeout(() => setCopyFeedback(null), 5000);
  };

  // === COPY LINK (live customer URL — only used inside Share modal now) ===
  const handleCopyLink = (refCode: string, accessKey?: string | null) => {
    const url = `https://peaceofsleep.vip/p/?ref=${refCode}&c=${countryCode.toLowerCase()}${keyParam(accessKey)}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopyFeedback('Link copied!');
      setTimeout(() => setCopyFeedback(null), 2000);
    });
  };

  const handleCopyAgreementLink = (refCode: string, accessKey?: string | null) => {
    const cc = countryCode.toLowerCase();
    const url = `https://peaceofsleep.vip/${cc}/agreement/?ref=${refCode}${keyParam(accessKey)}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopyFeedback('Agreement link copied!');
      setTimeout(() => setCopyFeedback(null), 2000);
    });
  };

  // === VIEW ===
  const handleView = async (proposal: Row) => {
    try {
      const data = await callEdgeFunction('get', { id: proposal.id });
      setViewProposal(data.proposal);
      setViewProposalTab('overview');
      // Load analytics events
      setEventsLoading(true);
      setProposalEvents([]);
      const { data: events } = await supabase
        .from('pos_proposal_events')
        .select('event_type, event_data, created_at')
        .eq('proposal_id', proposal.id)
        .order('created_at', { ascending: false })
        .limit(100);
      setProposalEvents(events || []);
      setEventsLoading(false);
      // Load version history
      try {
        const vData = await callEdgeFunction('get_versions', { id: proposal.id });
        setProposalVersions(vData.versions || []);
      } catch { setProposalVersions([]); }
    } catch (err: unknown) {
      alert('Error loading proposal: ' + errMsg(err));
    }
  };

  // === APPROVE (pending_approval → sent) ===
  const handleApproveProposal = async (id: string) => {
    try {
      await callEdgeFunction('approve', { id });
      await loadProposals();
    } catch (err: unknown) {
      alert('Error approving proposal: ' + errMsg(err));
    }
  };

  // === CLAIM SYSTEM LEAD (one-click) ===
  // Sets Sender + Assigned Manager to the current user on an
  // agency-attributed draft. Visible only on rows where
  // is_system_lead = true AND assigned_manager_id is null.
  const handleClaimLead = async (row: Row) => {
    if (!user?.id) return;
    const ok = window.confirm(`Claim ${row.reference_code}? You'll be set as both Sender and Assigned Manager.`);
    if (!ok) return;
    try {
      await callEdgeFunction('assign', { id: row.id, sender_user_id: user.id, assigned_manager_id: user.id, send_as_pos: true });
      await loadProposals();
    } catch (err: unknown) {
      alert('Error claiming lead: ' + errMsg(err));
    }
  };

  // === REJECT (pending_approval → rejected) ===
  const handleRejectProposal = async (id: string) => {
    const reason = prompt('Enter rejection reason:');
    if (!reason?.trim()) return;
    try {
      await callEdgeFunction('reject', { id, rejection_reason: reason.trim() });
      await loadProposals();
    } catch (err: unknown) {
      alert('Error rejecting proposal: ' + errMsg(err));
    }
  };

  // === OPEN EDIT ===
  const handleOpenEdit = async (row: { id: string }) => {
    try {
      const data = await callEdgeFunction('get', { id: row.id });
      const p = data.proposal;
      const pd = p.proposal_data || {};
      const scope = pd.scope || {};
      const pers = pd.personalisation || {};
      const amb = pd.ambassador || {};

      // Helper to convert saved items to ItemSelector format
      const toSelectorItems = (arr: ScopeItem[]): SelectedItem[] => (arr || []).map((it: ScopeItem, idx: number) => ({
        id: `edit-${idx}-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
        catalogItemId: it.item_id as string,
        zohoItemId: null,
        name: it.item_name as string,
        code: '',
        description: '',
        quantity: it.quantity || 1,
        unitPrice: it.unit_price || 0,
        discount: it.discount || 0,
        discountType: it.discount_type || 'percentage',
        unit: 'qty',
      }));

      // Detect room-mode: rooms with items[] inside
      const hasRoomItems = Array.isArray(scope.rooms) && scope.rooms.length > 0
        && scope.rooms.some((r: ScopeRoom) => Array.isArray(r.items) && r.items.length > 0);

      let savedItems: SelectedItem[] = [];
      let savedRooms: RoomEntry[] = [];
      let useRooms = false;

      if (hasRoomItems) {
        // New room+items shape
        useRooms = true;
        savedRooms = scope.rooms.map((r: ScopeRoom) => ({
          name: r.name || '',
          quantity: r.quantity || 1,
          items: toSelectorItems(r.items ?? []),
        }));
      } else if (Array.isArray(scope.rooms) && scope.rooms.length > 0) {
        // Legacy room shape (beds_small/beds_large)
        useRooms = true;
        savedRooms = scope.rooms.map((r: ScopeRoom) => ({
          name: r.name || '',
          quantity: r.quantity || 1,
          items: [],
        }));
        savedItems = toSelectorItems(scope.items);
      } else if (scope.items && scope.items.length > 0) {
        // Flat items from proposal_data
        savedItems = toSelectorItems(scope.items);
      } else {
        // Fallback: reconstruct items from top-level bed/topper columns
        // Handles proposals created before proposal_data was populated
        const fallbackItems = [];
        if (p.beds_small > 0) fallbackItems.push({ item_id: null, item_name: 'Beds: Small', quantity: p.beds_small, unit_price: 350, discount: 0, discount_type: 'percentage' });
        if (p.beds_large > 0) fallbackItems.push({ item_id: null, item_name: 'Beds: Large', quantity: p.beds_large, unit_price: 450, discount: 0, discount_type: 'percentage' });
        if (p.toppers_small > 0) fallbackItems.push({ item_id: null, item_name: 'Topper : Small', quantity: p.toppers_small, unit_price: 300, discount: 0, discount_type: 'percentage' });
        if (p.toppers_large > 0) fallbackItems.push({ item_id: null, item_name: 'Topper : Large', quantity: p.toppers_large, unit_price: 400, discount: 0, discount_type: 'percentage' });
        savedItems = toSelectorItems(fallbackItems);
      }

      setForm({
        property_name: p.property_name || '',
        property_type: p.property_type || '',
        property_city: p.property_city || '',
        property_province: p.property_province || '',
        contact_name: p.contact_name || '',
        contact_email: p.contact_email || '',
        contact_phone: p.contact_phone || '',
        selected_tier: p.selected_tier || 'assured',
        proposal_category: p.proposal_category || 'hospitality',
        proposal_audience: p.proposal_audience || '',
        status: p.status || 'draft',
        personal_note: p.personal_note || '',
        intro_paragraph: pers.intro_paragraph || '',
        scope_notes: (scope.notes || []).join('\n'),
        ambassador_photo_url: pers.ambassador_photo_url || '',
        ambassador_name: amb.name || '',
        ambassador_email: amb.email || '',
        ambassador_phone: amb.phone || '',
        ambassador_title: amb.title || INITIAL_FORM.ambassador_title,
        ambassador_quote: amb.quote || INITIAL_FORM.ambassador_quote,
        hero_image_url: p.hero_image_url || pers.hero_image_url || '',
        logo_url: p.logo_url || pers.logo_url || '',
        items: savedItems,
        use_rooms: useRooms,
        rooms: savedRooms,
        excludes_travel: pd.excludes_travel === true,
        volume_discount_pct_override: p.volume_discount_pct_override !== null && p.volume_discount_pct_override !== undefined ? String(p.volume_discount_pct_override) : '',
        volume_discount_amt_override: p.volume_discount_amt_override !== null && p.volume_discount_amt_override !== undefined ? String(p.volume_discount_amt_override) : '',
        assigned_ambassador_id: p.assigned_ambassador_id || '',
        assigned_manager_id: p.assigned_manager_id || '',
      });

      // Rehydrate any unsaved draft captured for this proposal (screen slept mid-edit)
      const draft = readDraft(p.id);
      if (draft) {
        setForm(draft);
        setDraftRestored(true);
      } else {
        setDraftRestored(false);
      }

      setEditingId(p.id);
      setUploadId(p.reference_code || Date.now().toString(36));
      setModalError(null);
      setShowModal(true);
    } catch (err: unknown) {
      alert('Error loading proposal: ' + errMsg(err));
    }
  };

  // === OPEN CREATE ===
  const handleOpenCreate = () => {
    const userName = [user?.first_name, user?.last_name].filter(Boolean).join(' ') || '';
    const fresh: FormState = {
      ...INITIAL_FORM,
      items: [],
      proposal_category: listTab === 'community-living' ? 'community-living' : 'hospitality',
      ambassador_name: userName,
      ambassador_email: user?.email || '',
      ambassador_phone: user?.phone || '',
    };
    // Rehydrate any unsaved draft from a previous session (screen slept, tab reloaded etc.)
    const draft = readDraft(null);
    if (draft) {
      setForm(draft);
      setDraftRestored(true);
    } else {
      setForm(fresh);
      setDraftRestored(false);
    }
    setEditingId(null);
    setUploadId(Date.now().toString(36));
    setModalError(null);
    setShowModal(true);
  };

  // Pre-fill the create modal when arriving from a POS Lead's "Build Proposal".
  // Lowest tier (Assured) for cold outreach; the bed split/price stays a manual
  // step in the builder before the admin reviews and sends.
  useEffect(() => {
    const st = location.state as {
      prefillFromLead?: {
        leadId?: string; property_name?: string; property_type?: string;
        contact_name?: string; contact_email?: string; contact_phone?: string;
        region?: string;
      };
      prefillGroupFromCollection?: {
        collectionId?: string; name?: string;
        contact_name?: string; contact_email?: string; contact_phone?: string;
        members?: string[];
      };
      openProposalId?: string;
    } | null;
    const userName = [user?.first_name, user?.last_name].filter(Boolean).join(' ') || '';
    const pf = st?.prefillFromLead;
    const gp = st?.prefillGroupFromCollection;
    const op = st?.openProposalId;
    if (op) {
      // Deep-link from the Leads list: open this specific proposal directly.
      handleOpenEdit({ id: op });
      navigate(location.pathname, { replace: true, state: {} });
      return;
    }
    if (pf) {
      setForm({
        ...INITIAL_FORM,
        items: [],
        proposal_category: 'hospitality',
        property_name: pf.property_name || '',
        property_type: pf.property_type || '',
        property_province: pf.region || '',
        contact_name: pf.contact_name || '',
        contact_email: pf.contact_email || '',
        contact_phone: pf.contact_phone || '',
        selected_tier: 'assured',
        ambassador_name: userName,
        ambassador_email: user?.email || '',
        ambassador_phone: user?.phone || '',
      });
      setEditingId(null);
      setUploadId(Date.now().toString(36));
      setModalError(null);
      setShowModal(true);
      pendingLeadIdRef.current = pf.leadId || null;
      navigate(location.pathname, { replace: true, state: {} });
    } else if (gp) {
      // Group hand-off from a Leads collection: pre-fill the group header at the
      // lowest tier; the user adds each member property (bed detail is manual).
      setListTab('group');
      setGroupForm({
        group_name: gp.name || '', contact_name: gp.contact_name || '',
        contact_email: gp.contact_email || '', contact_phone: gp.contact_phone || '',
        selected_tier: 'assured', personal_note: '', intro_paragraph: '', scope_notes: '',
        ambassador_photo_url: '', ambassador_name: userName, ambassador_email: user?.email || '',
        ambassador_phone: user?.phone || '', ambassador_title: 'Peace of Sleep™ Service Ambassador',
        ambassador_quote: 'If you have any questions about this proposal or about Peace of Sleep™, I\'m here to help. I look forward to hearing from you.',
        hero_image_url: '', logo_url: '', excludes_travel: false,
      });
      setGroupProperties([]);
      setGroupDraftRestored(false);
      setEditingGroupId(null);
      setGroupUploadId(Date.now().toString(36));
      setGroupModalError(null);
      setGroupPrefillMembers(gp.members || []);
      setShowGroupModal(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
    // Runs only when a new navigation hand-off arrives. Adding the other
    // referenced values (user, navigate, handleOpenEdit) would re-fire on token
    // refresh, since the user object is replaced on refresh, and re-open the modal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // === SUBMIT (create or update) ===
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setModalLoading(true);
      setModalError(null);

      const payload: Record<string, unknown> = {
        property_name: form.property_name,
        property_type: form.property_type,
        property_city: form.property_city,
        property_province: form.property_province,
        contact_name: form.contact_name,
        contact_email: form.contact_email,
        contact_phone: form.contact_phone,
        selected_tier: form.selected_tier,
        proposal_category: form.proposal_category || 'hospitality',
        proposal_audience: form.proposal_audience === '' ? null : (form.proposal_audience || undefined),
        personal_note: form.personal_note,
        intro_paragraph: form.intro_paragraph === '' ? null : (form.intro_paragraph || undefined),
        scope_notes: form.scope_notes === ''
          ? []
          : (form.scope_notes ? form.scope_notes.split('\n').filter((n: string) => n.trim()) : undefined),
        ambassador_photo_url: form.ambassador_photo_url === '' ? null : (form.ambassador_photo_url || undefined),
        ambassador_name: form.ambassador_name || undefined,
        ambassador_email: form.ambassador_email || undefined,
        ambassador_phone: form.ambassador_phone || undefined,
        ambassador_title: form.ambassador_title || undefined,
        ambassador_quote: form.ambassador_quote === '' ? null : (form.ambassador_quote || undefined),
        hero_image_url: form.hero_image_url === '' ? null : (form.hero_image_url || undefined),
        logo_url: form.logo_url === '' ? null : (form.logo_url || undefined),
        excludes_travel: form.excludes_travel,
        // Attribution. Only sent by admins (server enforces the same gate). null
        // clears the field; a value sets it.
        ...(isAdminUser ? {
          assigned_ambassador_id: form.assigned_ambassador_id || null,
          assigned_manager_id: form.assigned_manager_id || null,
        } : {}),
      };

      // Only admins / territory managers send the override. Empty string = null = clear/use auto.
      // Percentage and Rand amount are mutually exclusive; the server lets the
      // Rand amount win, but we also send both so a cleared field clears.
      if (canOverrideDiscount) {
        const v = form.volume_discount_pct_override.trim();
        payload.volume_discount_pct_override = v === '' ? null : Number(v);
        const a = form.volume_discount_amt_override.trim();
        payload.volume_discount_amt_override = a === '' ? null : Number(a);
      }

      // Save-time guards. Mirror of the server checks (see
      // pos-proposal-manage validateRawRooms / validateRawItems) so the
      // operator sees the issue inline instead of waiting for the round
      // trip. Server is the authoritative gate.
      const isBedItem = (name: string) => name === 'Beds: Large' || name === 'Beds: Small';

      // Room-mode: items live inside rooms
      if (form.use_rooms && form.rooms.length > 0) {
        for (let i = 0; i < form.rooms.length; i++) {
          const r = form.rooms[i];
          if (!r) continue;
          const roomLabel = r.name?.trim() || `Room ${i + 1}`;
          const qty = parseInt(String(r.quantity ?? ''));
          if (!Number.isFinite(qty) || qty < 1) {
            setModalError(`${roomLabel}: quantity must be at least 1.`);
            return;
          }
          const roomItems = r.items || [];
          if (roomItems.length === 0) {
            setModalError(`${roomLabel}: add at least one item.`);
            return;
          }
          for (const it of roomItems) {
            const itemName = it.name || 'Item';
            if (!Number.isFinite(it.quantity) || it.quantity < 1) {
              setModalError(`${roomLabel} — ${itemName}: quantity must be at least 1.`);
              return;
            }
            if (!Number.isFinite(it.unitPrice) || it.unitPrice <= 0) {
              setModalError(`${roomLabel} — ${itemName}: must be priced above zero.`);
              return;
            }
          }
        }

        type RoomItemPayload = { item_id: string | null; item_name: string; quantity: number; unit_price: number; discount: number; discount_type: string };
        type RoomPayload = { name: string; quantity: number; items: RoomItemPayload[] };

        const roomsWithItems: RoomPayload[] = form.rooms.map((r): RoomPayload => ({
          name: r.name,
          quantity: parseInt(String(r.quantity ?? '')) || 1,
          items: (r.items || []).map((it): RoomItemPayload => ({
            item_id: it.catalogItemId ?? null,
            item_name: it.name,
            quantity: it.quantity,
            unit_price: it.unitPrice,
            discount: it.discount || 0,
            discount_type: it.discountType || 'percentage',
          })),
        }));

        // Validate: at least 1 item across all rooms (belt-and-braces;
        // the per-room check above already blocks empty rooms).
        const totalItems = roomsWithItems.reduce((s: number, r: RoomPayload) => s + r.items.length, 0);
        if (totalItems < 1) {
          setModalError('At least 1 item is required. Add items to your rooms.');
          return;
        }

        // Must include at least one Beds: Large or Beds: Small.
        const bedQty = roomsWithItems.reduce((sum: number, r: RoomPayload) => {
          const roomBeds = r.items
            .filter((it: RoomItemPayload) => isBedItem(it.item_name))
            .reduce((s: number, it: RoomItemPayload) => s + it.quantity, 0);
          return sum + roomBeds * r.quantity;
        }, 0);
        if (bedQty < 1) {
          setModalError('Proposal must include at least one bed (Beds: Large or Beds: Small). Add a bed line before saving.');
          return;
        }

        payload.rooms = roomsWithItems;
      } else if (form.items.length > 0) {
        for (const it of form.items) {
          const itemName = it.name || 'Item';
          if (!Number.isFinite(it.quantity) || it.quantity < 1) {
            setModalError(`${itemName}: quantity must be at least 1.`);
            return;
          }
          if (!Number.isFinite(it.unitPrice) || it.unitPrice <= 0) {
            setModalError(`${itemName}: must be priced above zero.`);
            return;
          }
        }

        // Must include at least one Beds: Large or Beds: Small.
        const bedQty = form.items
          .filter(it => isBedItem(it.name))
          .reduce((s, it) => s + it.quantity, 0);
        if (bedQty < 1) {
          setModalError('Proposal must include at least one bed (Beds: Large or Beds: Small). Add a bed line before saving.');
          return;
        }

        // Flat items flow
        payload.items = form.items.map((it: SelectedItem) => ({
          item_id: it.catalogItemId,
          item_name: it.name,
          quantity: it.quantity,
          unit_price: it.unitPrice,
          discount: it.discount || 0,
          discount_type: it.discountType || 'percentage',
        }));
      } else {
        setModalError('At least 1 item is required. Use the item picker to add services.');
        return;
      }

      let data;
      if (editingId) {
        data = await callEdgeFunction('update', { id: editingId, ...payload });
      } else {
        data = await callEdgeFunction('create', payload);
      }

      // Successful save — clear any persisted draft for this slot and close.
      clearDraft(editingId);
      setDraftRestored(false);
      setShowModal(false);

      // If this proposal was started from a POS Lead, link it back and advance
      // the lead. Best-effort: never let this block the proposal itself.
      if (!editingId && pendingLeadIdRef.current) {
        const newProposalId = data?.proposal?.id;
        const leadId = pendingLeadIdRef.current;
        pendingLeadIdRef.current = null;
        if (newProposalId) {
          await supabase
            .from('pos_prospect_properties')
            .update({ proposal_id: newProposalId, status: 'proposal_sent', updated_by: user?.id ?? null })
            .eq('id', leadId);
        }
      }

      if (isAmbassador) {
        // Ambassador creates as draft — no link to copy yet
        setCopyFeedback(editingId ? 'Proposal updated.' : 'Draft created. Submit for approval when ready.');
        setTimeout(() => setCopyFeedback(null), 4000);
      } else {
        // Admin: auto-copy link
        const url = data.proposal_url || `https://peaceofsleep.vip/p/?ref=${data.reference_code || data.proposal?.reference_code}&c=${countryCode.toLowerCase()}${keyParam(data.proposal?.access_key)}`;
        navigator.clipboard.writeText(url).then(() => {
          setCopyFeedback(editingId ? 'Updated! Link copied.' : 'Created! Link copied.');
          setTimeout(() => setCopyFeedback(null), 3000);
        });
      }

      loadProposals();
    } catch (err: unknown) {
      setModalError(errMsg(err));
    } finally {
      setModalLoading(false);
    }
  };

  // === DELETE ===
  const handleDelete = async () => {
    if (!editingId) return;
    if (!confirm('Delete this proposal? This cannot be undone.')) return;

    try {
      setModalLoading(true);
      await callEdgeFunction('delete', { id: editingId });
      setShowModal(false);
      setCopyFeedback('Proposal deleted.');
      setTimeout(() => setCopyFeedback(null), 2000);
      loadProposals();
    } catch (err: unknown) {
      setModalError('Delete failed: ' + errMsg(err));
    } finally {
      setModalLoading(false);
    }
  };

  // === SUBMIT FOR APPROVAL (ambassador drafts) ===
  const handleSubmitForApproval = async (id: string) => {
    if (isAmbassador && !submitUnlocked) {
      alert('Complete your training to unlock Submit Proposal. Visit the Training Academy in your portal to see what is outstanding.');
      return;
    }
    if (!confirm('Submit this proposal for admin approval?')) return;
    try {
      await callEdgeFunction('submit_for_approval', { id });
      setCopyFeedback('Submitted for approval!');
      setTimeout(() => setCopyFeedback(null), 3000);
      loadProposals();
    } catch (err: unknown) {
      alert('Error: ' + errMsg(err));
    }
  };

  // === ONBOARD INDIVIDUAL PROPERTY ===
  const [propertyOnboardPreview, setPropertyOnboardPreview] = useState<PropertyOnboardPreviewData | null>(null);
  const [propertyOnboardManagerId, setPropertyOnboardManagerId] = useState('');
  const [propertyOnboardCustomer, setPropertyOnboardCustomer] = useState<CustomerChoice | null>(null);
  // The full active customer list, so the picker is a real search and not just
  // the auto-suggested matches (a customer filed under a different name/email
  // would otherwise be unreachable, forcing a duplicate).
  const [onboardCustomerList, setOnboardCustomerList] = useState<Array<{ id: string; customer_name: string; company_name: string | null; email: string | null }>>([]);

  const handlePropertyOnboardPreview = async (proposalId: string) => {
    try {
      setOnboardLoading(true);
      const data = await callOnboardFunction('preview_property', { proposal_id: proposalId });
      if (data.already_onboarded) {
        alert('This property has already been onboarded.');
        return;
      }
      const { data: custs } = await supabase
        .from('customers')
        .select('id, customer_name, company_name, email')
        .eq('status', 'active')
        .order('customer_name', { ascending: true })
        .limit(10000);
      setOnboardCustomerList((custs ?? []) as Array<{ id: string; customer_name: string; company_name: string | null; email: string | null }>);
      setPropertyOnboardPreview(data);
      setPropertyOnboardManagerId('');
      const top = (data?.proposal?.customer_suggestions ?? [])[0];
      setPropertyOnboardCustomer(top?.id ? { mode: 'existing', customer_id: top.id } : null);
    } catch (err: unknown) {
      alert('Error: ' + errMsg(err));
    } finally {
      setOnboardLoading(false);
    }
  };

  const handlePropertyOnboardConfirm = async () => {
    if (!propertyOnboardPreview?.proposal) return;
    if (!propertyOnboardManagerId) {
      alert('Pick an assigned manager before onboarding.');
      return;
    }
    if (!propertyOnboardCustomer) {
      alert('Confirm the existing customer or pick Create new before onboarding.');
      return;
    }
    try {
      setOnboardLoading(true);
      const data = await callOnboardFunction('onboard_property', {
        proposal_id: propertyOnboardPreview.proposal.id,
        manager_id: propertyOnboardManagerId,
        customer_choice: propertyOnboardCustomer,
      });
      setPropertyOnboardPreview(null);
      setPropertyOnboardCustomer(null);
      if (data?.property_id) {
        // Take the user straight to the new property's certification view
        // rather than back to the full proposals list.
        navigate(`/pos-properties?property=${data.property_id}`);
      } else {
        setCopyFeedback(`"${data.property_name || 'Property'}" onboarded — ${data.rooms_created} rooms created`);
        setTimeout(() => setCopyFeedback(null), 4000);
        loadProposals();
      }
    } catch (err: unknown) {
      alert('Onboard failed: ' + errMsg(err));
    } finally {
      setOnboardLoading(false);
    }
  };

  // === ROOM HELPERS ===
  const addRoom = () => {
    setForm(f => ({
      ...f,
      rooms: [...f.rooms, { name: '', quantity: 1, items: [] }],
    }));
  };

  const updateRoom = (index: number, field: string, value: string | number | SelectedItem[]) => {
    setForm(f => ({
      ...f,
      rooms: f.rooms.map((r, i) => i === index ? { ...r, [field]: value } : r),
    }));
  };

  const removeRoom = (index: number) => {
    setForm(f => ({
      ...f,
      rooms: f.rooms.filter((_, i) => i !== index),
    }));
  };

  const moveRoom = (index: number, direction: string) => {
    const target = direction === 'up' ? index - 1 : index + 1;
    setForm(f => {
      if (target < 0 || target >= f.rooms.length) return f;
      const newRooms = [...f.rooms];
      [newRooms[index], newRooms[target]] = [newRooms[target]!, newRooms[index]!];
      return { ...f, rooms: newRooms };
    });
  };

  // Table columns
  const columns = [
    {
      key: 'reference_code',
      label: 'Reference',
      sortable: true,
      render: (row: Row) => <code className="text-sm">{row.reference_code}</code>,
    },
    {
      key: 'property_name',
      label: 'Property',
      sortable: true,
      render: (row: Row) => {
        if (row._isGroup) {
          const open = expandedGroups.has(row.id) || listSearch.trim().length > 0;
          const count = (row._group?.property_count ?? row._members?.length ?? 0) as number;
          return (
            <div>
              <div className="fw-500">
                {row.property_name}
                <span className="pos-proposal-badge-group">Group</span>
                <span className="pos-proposal-caret">{open ? '▾' : '▸'}</span>
              </div>
              <div className="text-sm text-muted">{count} {count === 1 ? 'property' : 'properties'}</div>
            </div>
          );
        }
        return (
        <div>
          <div className="fw-500">
            {row.property_name}
            {row.is_system_lead && (
              <span
                className="pos-proposal-badge pos-proposal-badge-system"
                title="Auto-attributed to POS Leads Agency from a paid-traffic landing-page submission. No human created this proposal."
              >
                SYSTEM LEAD
              </span>
            )}
            {row.group_proposal_id && (
              <span className="pos-proposal-badge pos-proposal-badge-grp">
                GRP{row.pos_group_proposals?.group_name ? `: ${row.pos_group_proposals.group_name}` : ''}
              </span>
            )}
          </div>
          {row.property_city && <div className="text-sm text-muted">{row.property_city}</div>}
        </div>
        );
      },
    },
    {
      key: 'contact_name',
      label: 'Contact',
      sortable: true,
      render: (row: Row) => (
        <div>
          <div>{row.contact_name}</div>
          {row.contact_email && <div className="text-sm text-muted">{row.contact_email}</div>}
        </div>
      ),
    },
    {
      key: 'assigned_ambassador_name',
      label: 'Ambassador',
      sortable: true,
      render: (row: Row) => row.assigned_ambassador_name || <span className="text-muted">No Ambassador</span>,
    },
    {
      key: 'annual_total',
      label: 'Annual Total',
      sortable: true,
      render: (row: Row) => formatCurrency(row.annual_total),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (row: Row) => {
        const cfg = (STATUS_CONFIG[row.status] || STATUS_CONFIG.draft)!;
        return <StatusBadge bg={cfg.bg} color={cfg.color}>{cfg.icon} {cfg.label}</StatusBadge>;
      },
    },
    ...(!isAmbassador ? [{
      key: 'ambassador_name',
      label: 'Created By',
      sortable: true,
      render: (row: Row) => row.ambassador_name || <span className="text-muted">—</span>,
    }] : []),
    {
      key: 'created_at',
      label: 'Created',
      sortable: true,
      render: (row: Row) => formatDate(row.created_at),
    },
    {
      key: 'follow_up',
      label: 'Follow-up',
      sortable: true,
      align: 'center' as const,
      render: (row: Row) => {
        const temp = computeProposalTemperature(row);
        if (!temp) return <span className="text-muted">—</span>;
        const age = proposalAgeDays(row);
        return (
          <span
            className={`status-pill status-pill--temp-${temp}`}
            title={PROPOSAL_TEMPERATURE_CONFIG[temp].action}
          >
            {age !== null ? `${age}d` : PROPOSAL_TEMPERATURE_CONFIG[temp].label}
          </span>
        );
      },
    },
    {
      key: 'followed_up',
      label: 'Followed up',
      sortable: true,
      render: (row: Row) => {
        const fu = followUps.get(row.id as string);
        if (!fu?.emailAt && !fu?.whatsAppAt) return <span className="text-muted">—</span>;
        return (
          <div>
            {fu.emailAt && <div><span className="status-pill status-pill--completed">✓ Email {formatDate(fu.emailAt)}</span></div>}
            {fu.whatsAppAt && <div><span className="status-pill status-pill--completed">✓ WhatsApp {formatDate(fu.whatsAppAt)}</span></div>}
          </div>
        );
      },
    },
  ];

  // Build a Created By filter from unique creator names in the loaded proposals.
  const creatorFilter = useMemo(() => {
    const names = Array.from(new Set(proposals.map(p => p.ambassador_name).filter(Boolean) as string[]))
      .sort((a, b) => a.localeCompare(b));
    return {
      key: 'ambassador_name',
      label: 'Created By',
      options: names.map(n => ({ value: n, label: n })),
    };
  }, [proposals]);

  // Filter by the appointed POS Ambassador. Includes an explicit "No Ambassador"
  // bucket for proposals with none, matched via matchFn since null can't be
  // compared by plain equality.
  const ambassadorFilter = useMemo<Filter>(() => {
    const names = Array.from(new Set(proposals.map(p => p.assigned_ambassador_name).filter(Boolean) as string[]))
      .sort((a, b) => a.localeCompare(b));
    const hasNone = proposals.some(p => !p.assigned_ambassador_name);
    return {
      key: 'assigned_ambassador_name',
      label: 'Ambassador',
      options: [
        ...(hasNone ? [{ value: '__none__', label: 'No Ambassador' }] : []),
        ...names.map(n => ({ value: n, label: n })),
      ],
      matchFn: (row, value) => value === '__none__'
        ? !row.assigned_ambassador_name
        : row.assigned_ambassador_name === value,
    };
  }, [proposals]);

  // Follow-up temperature filter, so the screen can open on just the reds.
  // Settled proposals carry no temperature and are matched by "Settled".
  const followUpFilter = useMemo<Filter>(() => ({
    key: 'follow_up',
    label: 'Follow-up',
    options: [
      { value: 'green', label: 'Green, nothing due' },
      { value: 'yellow', label: 'Yellow, check in' },
      { value: 'red', label: 'Red, needs a push' },
      { value: '__settled__', label: 'Settled' },
    ],
    matchFn: (row, value) => {
      const temp = computeProposalTemperature(row);
      return value === '__settled__' ? temp === null : temp === value;
    },
  }), []);

  // Followed up filter, so the sender can work down who has not had one yet.
  const followedUpFilter = useMemo<Filter>(() => ({
    key: 'followed_up',
    label: 'Followed up',
    options: [
      { value: 'none', label: 'Not followed up yet' },
      { value: 'email', label: 'Email sent' },
      { value: 'whatsapp', label: 'WhatsApp sent' },
    ],
    matchFn: (row, value) => {
      const fu = followUps.get(row.id as string);
      if (value === 'email') return !!fu?.emailAt;
      if (value === 'whatsapp') return !!fu?.whatsAppAt;
      return !fu?.emailAt && !fu?.whatsAppAt;
    },
  }), [followUps]);

  // Metrics view aggregation. Groups loaded proposals by the underlying user
  // record (ambassador_user_id) so a typo on a single proposal's name field
  // can't fragment a person into two rows. Display name picks the most
  // common non-empty value across that user's proposals.
  //
  // "Sent" = proposals that actually went out to a customer. Drafts, pending-
  // approval and admin-rejected items are excluded so the win rate reflects
  // closing skill, not draft volume.
  // "Accepted" = the green Accepted badge on the board (status = 'accepted').
  // Win rate = Accepted / Sent.
  //
  // Ambassadors only ever see their own proposals (server-side scope), so
  // the metrics naturally collapse to a single row for them.
  const metricsData = useMemo(() => {
    const SENT_STATUSES = new Set(['published', 'viewed', 'accepted', 'expired']);
    const now = Date.now();
    const cutoff = metricsRange === '30d' ? now - 30 * 86400000
      : metricsRange === '90d' ? now - 90 * 86400000
      : 0;

    const scoped = proposals.filter(p => {
      const cat = (p.proposal_category as string) || 'hospitality';
      if (listTab === 'community-living' ? cat !== 'community-living' : cat !== 'hospitality') return false;
      if (cutoff > 0) {
        const created = p.created_at ? new Date(p.created_at as string).getTime() : 0;
        if (!created || created < cutoff) return false;
      }
      return true;
    });

    type Bucket = { id: string; sent: number; accepted: number; nameCounts: Map<string, number> };
    const buckets = new Map<string, Bucket>();
    for (const p of scoped) {
      if (!SENT_STATUSES.has(p.status as string)) continue;
      const id = (p.ambassador_user_id as string) || '__unattributed__';
      const name = ((p.ambassador_name as string) || '').trim();
      const bucket = buckets.get(id) || { id, sent: 0, accepted: 0, nameCounts: new Map<string, number>() };
      bucket.sent += 1;
      if (p.status === 'accepted') bucket.accepted += 1;
      if (name) bucket.nameCounts.set(name, (bucket.nameCounts.get(name) || 0) + 1);
      buckets.set(id, bucket);
    }

    const rows = Array.from(buckets.values())
      .map(b => {
        let displayName = 'Unattributed';
        if (b.id !== '__unattributed__') {
          let top = ''; let topCount = 0;
          for (const [n, c] of b.nameCounts) if (c > topCount) { top = n; topCount = c; }
          displayName = top || 'Unattributed';
        }
        return {
          id: b.id,
          name: displayName,
          sent: b.sent,
          accepted: b.accepted,
          winRate: b.sent > 0 ? (b.accepted / b.sent) * 100 : 0,
        };
      })
      .sort((a, b) => b.sent - a.sent || b.accepted - a.accepted || a.name.localeCompare(b.name));

    const totalSent = rows.reduce((s, r) => s + r.sent, 0);
    const totalAccepted = rows.reduce((s, r) => s + r.accepted, 0);
    return {
      rows,
      totals: {
        sent: totalSent,
        accepted: totalAccepted,
        winRate: totalSent > 0 ? (totalAccepted / totalSent) * 100 : 0,
      },
    };
  }, [proposals, listTab, metricsRange]);

  // Board view: tab-scoped + search + filtered. Driven from the same source
  // as the list view so the two views never disagree on which proposals
  // a user can see.
  const boardProposals = useMemo(() => {
    // Grouped properties are collapsed into their group, so they are not shown
    // as loose cards on the board. The group is managed from the list/Groups tab.
    const tabScoped = proposals.filter(p =>
      !p.group_proposal_id && (
        listTab === 'community-living'
          ? p.proposal_category === 'community-living'
          : (p.proposal_category || 'hospitality') === 'hospitality'
      )
    );

    const search = boardSearch.trim().toLowerCase();
    return tabScoped.filter(p => {
      if (search) {
        const haystack = [
          p.reference_code, p.property_name, p.contact_name, p.contact_email, p.ambassador_name,
        ].filter(Boolean).join(' ').toLowerCase();
        if (!haystack.includes(search)) return false;
      }
      if (boardTierFilter && p.selected_tier !== boardTierFilter) return false;
      if (boardAmbassadorFilter && p.ambassador_name !== boardAmbassadorFilter) return false;
      return true;
    });
  }, [proposals, listTab, boardSearch, boardTierFilter, boardAmbassadorFilter]);

  // List view rows: standalone proposals for the tab plus one expandable row per
  // group in the tab. _groupSearch folds in member text so the table's search
  // surfaces (and the sub-row opens) a group when one of its properties matches.
  const listData = useMemo(() => {
    const inCat = (c: string | undefined) =>
      listTab === 'community-living' ? c === 'community-living' : (c || 'hospitality') === 'hospitality';
    const standalone = proposals.filter(p => !p.group_proposal_id && inCat(p.proposal_category as string | undefined));
    const groupRows = isAmbassador ? [] : groupProposals.filter(g => inCat(g.proposal_category as string | undefined)).map(g => {
      const members = proposals.filter(p => p.group_proposal_id === g.id);
      const memberBlob = members
        .map(m => [m.property_name, m.property_city, m.contact_name, m.contact_email, m.reference_code].filter(Boolean).join(' '))
        .join(' ');
      return {
        id: g.id,
        _isGroup: true,
        _group: g,
        _members: members,
        _groupSearch: [g.group_name, g.reference_code, g.contact_name, memberBlob].filter(Boolean).join(' '),
        reference_code: g.reference_code,
        property_name: g.group_name,
        property_city: null,
        contact_name: g.contact_name,
        contact_email: g.contact_email,
        selected_tier: g.selected_tier,
        annual_total: g.annual_total,
        status: g.status,
        ambassador_name: g.ambassador_name || null,
        assigned_ambassador_name: g.assigned_ambassador_name || null,
        created_at: g.created_at,
      } as unknown as Row;
    });
    // Days since sending, carried on the row so the Follow-up column can be
    // sorted (oldest first is the chase order). Settled rows sort last.
    return [...standalone, ...groupRows].map(r => ({
      ...r,
      follow_up: computeProposalTemperature(r) ? proposalAgeDays(r) : -1,
      // Latest follow-up of either kind, so the column sorts by when it went out.
      followed_up: (() => {
        const fu = followUps.get(r.id as string);
        return [fu?.emailAt, fu?.whatsAppAt].filter(Boolean).sort().pop() ?? '';
      })(),
    }));
  }, [proposals, groupProposals, listTab, isAmbassador, followUps]);

  const toggleGroupRow = (id: string) => setExpandedGroups(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  // Action icons rendered on each row (list view) and each card (board view).
  // Same logic in both places — admin-only Approve/Reject, ambassador-only
  // Submit, locked-state Edit, etc. — so it lives here once and gets passed
  // through to both DataTable and ProposalsBoardView.
  const proposalActions = (row: Row) => (
    <>
      <span className="action-icon" onClick={() => handleView(row)} title="View proposal">👁️</span>
      <span className="action-icon" onClick={() => handleOpenPreview(row.reference_code, row.access_key)} title="Preview as customer (admin-only — view does not count)">🔍</span>
      {row.is_locked && isAmbassador ? (
        <span className="action-icon action-icon-locked" title="Locked — accepted">🔒</span>
      ) : row.is_locked ? (
        <span className="action-icon action-icon-warning" onClick={() => {
          if (confirm('This proposal was accepted by the customer. Changes will be versioned. Continue?')) handleOpenEdit(row);
        }} title="Edit (accepted — changes versioned)">✏️</span>
      ) : row.group_proposal_id && !isAmbassador ? (
        <span className="action-icon action-icon-group" onClick={() => handleOpenGroupEdit({ id: row.group_proposal_id as string })} title={`Edit in group: ${row.pos_group_proposals?.group_name || 'Group'}`}>📎</span>
      ) : isAmbassador && !['draft', 'rejected'].includes(row.status) ? (
        <span className="action-icon action-icon-locked" title="Cannot edit — submitted">✏️</span>
      ) : (
        <span className="action-icon" onClick={() => handleOpenEdit(row)} title="Edit proposal">✏️</span>
      )}
      {row.status !== 'accepted' && (
        <span className="action-icon" onClick={() => setSendingProposal(row)} title="Share proposal">📤</span>
      )}
      {row.status === 'draft' && !isAmbassador && !row.group_proposal_id && (
        <span className="action-icon action-icon-info" onClick={() => void handleQuickMarkSent(row)} title="Mark as sent">📨</span>
      )}
      {['published', 'viewed'].includes(row.status) && !row._isGroup && (row.contact_email || row.contact_phone) && (
        <span
          className="action-icon"
          onClick={() => setFollowUpProposal(row)}
          title="Send follow-up"
        >
          📩
        </span>
      )}
      {!isAmbassador && row.is_system_lead && !row.assigned_manager_id && (
        <span
          className="action-icon action-icon--success"
          onClick={() => void handleClaimLead(row)}
          title="Claim this lead (sets you as Sender and Assigned Manager)"
        >
          🙋
        </span>
      )}
      {!isAmbassador && (
        <span
          className="action-icon"
          onClick={() => setAssigningProposal(row)}
          title={row.sender_user_id ? 'Reassign Sender / Manager' : 'Assign Sender / Manager'}
          style={{ color: row.sender_user_id ? undefined : 'var(--color-error)' }}
        >
          👤
        </span>
      )}
      {row.signed_pdf_url && (
        <span className="action-icon action-icon-success" onClick={() => void openSignedAgreement(row.signed_pdf_url, 'Signed Agreement')} title="View signed agreement">📝</span>
      )}
      {row.status === 'pending_approval' && !isAmbassador && (
        <>
          <span className="action-icon action-icon--success" onClick={() => handleApproveProposal(row.id)} title="Approve proposal">✅</span>
          <span className="action-icon action-icon--danger" onClick={() => handleRejectProposal(row.id)} title="Reject proposal">❌</span>
        </>
      )}
      {row.status === 'draft' && isAmbassador && (
        submitUnlocked
          ? <span className="action-icon action-icon-warning" onClick={() => handleSubmitForApproval(row.id)} title="Submit for approval">📤</span>
          : <span className="action-icon action-icon-muted" onClick={() => handleSubmitForApproval(row.id)} title="Locked — complete your training to unlock">🔒</span>
      )}
      {!row.group_proposal_id && !isAmbassador && row.status === 'accepted' && row.property_id && (
        <span className="action-icon" onClick={() => navigate(`/pos-properties?property=${row.property_id}`)} title="View property">🏨</span>
      )}
      {!row.group_proposal_id && !isAmbassador && !row.property_id && ['published', 'viewed', 'accepted'].includes(row.status) && (
        <span
          className="action-icon action-icon-info"
          onClick={() => handlePropertyOnboardPreview(row.id)}
          title={row.status === 'accepted' ? 'Onboard property' : 'Mark accepted and onboard'}
        >
          🏢
        </span>
      )}
      <ProposalMoveTo
        proposal={row}
        supabase={supabase}
        isAmbassador={isAmbassador}
        onMoved={loadProposals}
        onOpenShare={() => setSendingProposal(row)}
        onSubmitForApproval={() => handleSubmitForApproval(row.id)}
      />
    </>
  );

  // Action icons for a group row (shared by the inline list rows and the
  // dedicated Groups tab). Receives the raw group record.
  const groupActions = (row: Row) => (
    <>
      <span className="action-icon" onClick={() => handleViewGroup(row)} title="View proposal">👁️</span>
      <span className="action-icon" onClick={() => handleOpenPreview(row.reference_code, row.access_key)} title="Preview as customer (admin-only, view does not count)">🔍</span>
      {row.is_locked ? (
        <span className="action-icon action-icon-locked-half" title="Locked, accepted">🔒</span>
      ) : (
        <span className="action-icon" onClick={() => handleOpenGroupEdit(row)} title="Edit proposal">✏️</span>
      )}
      {row.status !== 'accepted' && (
        <span className="action-icon" onClick={() => setSendingProposal(row)} title="Share proposal">📤</span>
      )}
      {row.status === 'draft' && (
        <span className="action-icon action-icon-info" onClick={() => void handleQuickMarkSent(row)} title="Mark as sent">📨</span>
      )}
      {row.signed_pdf_url && (
        <span className="action-icon action-icon-success" onClick={() => void openSignedAgreement(row.signed_pdf_url, 'Signed Agreement')} title="View signed agreement">📝</span>
      )}
      {(row.status === 'published' || row.status === 'viewed' || row.status === 'accepted') && !(row.onboarded_count > 0 && row.onboarded_count >= row.property_count) && (
        <span className="action-icon action-icon-success" onClick={() => handleOnboardPreview(row)} title={row.onboarded_count > 0 ? `Onboard properties (${row.onboarded_count}/${row.property_count} done)` : 'Onboard properties'}>🏨</span>
      )}
    </>
  );

  // Member properties shown beneath an expanded group row. Opens automatically
  // when a search matches a member, showing just the matches.
  const renderGroupSubRow = (row: Row) => {
    if (!row._isGroup) return null;
    const members: Row[] = row._members || [];
    const q = listSearch.trim().toLowerCase();
    const matched = q
      ? members.filter(m => [m.property_name, m.property_city, m.contact_name, m.contact_email, m.reference_code]
          .filter(Boolean).some(v => String(v).toLowerCase().includes(q)))
      : [];
    const open = expandedGroups.has(row.id) || q.length > 0;
    if (!open) return null;
    const membersToShow = q ? (matched.length ? matched : members) : members;
    if (membersToShow.length === 0) return null;
    return (
      <div className="pos-proposal-members">
        {membersToShow.map(m => {
          const cfg = (STATUS_CONFIG[m.status] || STATUS_CONFIG.draft)!;
          return (
            <div key={m.id} className="pos-proposal-member-row">
              <div className="pos-proposal-member-main">
                <div className="fw-500">{m.property_name}</div>
                <div className="text-sm text-muted">{[m.property_city, m.contact_name].filter(Boolean).join(' · ')}{m.reference_code ? ` · ${m.reference_code}` : ''}</div>
              </div>
              <div className="pos-proposal-member-meta">
                <span className="text-sm text-muted">{TIER_LABELS[m.selected_tier] || m.selected_tier}</span>
                <span className="text-sm pos-proposal-gold-amount">{formatCurrency(m.annual_total)}</span>
                <StatusBadge bg={cfg.bg} color={cfg.color}>{cfg.icon} {cfg.label}</StatusBadge>
                <div className="table-actions" onClick={e => e.stopPropagation()}>{proposalActions(m)}</div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Permission guards — placed after all hooks so the hook call order stays
  // stable across renders (rules-of-hooks). The derived consts above are
  // null-safe while permission is loading and proposals is still empty.
  if (permissionLoading) {
    return <div className="placeholder-page"><div className="spinner" /></div>;
  }
  if (!canAccess) {
    return <AccessDenied feature="pos_proposals" />;
  }
  if (error) {
    return <ErrorDisplay error={error} onRetry={loadProposals} />;
  }

  return (
    <div className="page-container pos-themed">
      <div className="page-header">
        <div>
          <h1 className="page-header-title">{isAmbassador ? 'My Proposals' : 'POS Proposals'}</h1>
          <p className="page-header-subtitle">
            {isAmbassador
              ? 'Your proposal pipeline. Leads from your referral link arrive here as drafts automatically. You can also start a fresh proposal yourself for any property you have spoken to, submit it for review, and watch its progress here.'
              : 'Create and manage Peace of Sleep certification proposals'}
          </p>
        </div>
        <button
          className="btn btn-primary"
          onClick={isAmbassador ? handleOpenCreate : (listTab === 'group' ? handleOpenGroupCreate : handleOpenCreate)}
        >
          + New {!isAmbassador && listTab === 'group' ? 'Group' : listTab === 'community-living' ? 'Community Living Proposal' : 'Proposal'}
        </button>
      </div>

      <div className="page-tabs">
        <button className={`page-tab ${listTab === 'hospitality' ? 'active' : ''}`} onClick={() => setListTab('hospitality')}>
          {isAmbassador ? 'My Proposals' : 'Hospitality'} <span className="tab-badge">{proposals.filter(p => !p.group_proposal_id && (p.proposal_category || 'hospitality') === 'hospitality').length + (isAmbassador ? 0 : groupProposals.filter(g => (g.proposal_category || 'hospitality') === 'hospitality').length)}</span>
        </button>
        {!isAmbassador && (
          <button className={`page-tab ${listTab === 'community-living' ? 'active' : ''}`} onClick={() => setListTab('community-living')}>
            Community Living <span className="tab-badge">{proposals.filter(p => !p.group_proposal_id && p.proposal_category === 'community-living').length + groupProposals.filter(g => g.proposal_category === 'community-living').length}</span>
          </button>
        )}
        {!isAmbassador && (
          <button className={`page-tab ${listTab === 'group' ? 'active' : ''}`} onClick={() => setListTab('group')}>
            Groups <span className="tab-badge secondary">{groupProposals.length}</span>
          </button>
        )}
      </div>

      {copyFeedback && (
        <div className="toast toast-success">
          {copyFeedback}
        </div>
      )}

      {(listTab === 'hospitality' || listTab === 'community-living') && (
        <div className="page-tabs pos-proposal-view-tabs">
          <button
            className={`page-tab ${viewMode === 'list' ? 'active' : ''}`}
            onClick={() => setViewMode('list')}
          >
            List
          </button>
          <button
            className={`page-tab ${viewMode === 'board' ? 'active' : ''}`}
            onClick={() => setViewMode('board')}
          >
            Board
          </button>
          <button
            className={`page-tab ${viewMode === 'metrics' ? 'active' : ''}`}
            onClick={() => setViewMode('metrics')}
          >
            Metrics
          </button>
        </div>
      )}

      {(listTab === 'hospitality' || listTab === 'community-living') && viewMode === 'board' && (
        <>
          <div className="list-toolbar">
            <div className="list-toolbar-left">
              <div className="list-search">
                <span className="list-search-icon">&#x1F50D;</span>
                <input
                  type="text"
                  placeholder="Search reference, property, contact, ambassador..."
                  value={boardSearch}
                  onChange={(e) => setBoardSearch(e.target.value)}
                />
                {boardSearch && (
                  <button className="list-search-clear" onClick={() => setBoardSearch('')}>&times;</button>
                )}
              </div>

              <SearchSelect
                width="inline"
                options={[
                  { value: '', label: 'Tier: All' },
                  { value: 'assured', label: 'Assured' },
                  { value: 'distinguished', label: 'Distinguished' },
                  { value: 'signature', label: 'Signature' },
                ]}
                value={boardTierFilter}
                onChange={setBoardTierFilter}
              />

              {!isAmbassador && creatorFilter.options.length > 0 && (
                <SearchSelect
                  width="inline"
                  options={[
                    { value: '', label: 'Created by: All' },
                    ...creatorFilter.options.map(opt => ({ value: opt.value, label: opt.label })),
                  ]}
                  value={boardAmbassadorFilter}
                  onChange={setBoardAmbassadorFilter}
                />
              )}

              {(boardSearch || boardTierFilter || boardAmbassadorFilter) && (
                <button
                  className="btn btn-link pos-proposal-clear-link"
                  onClick={() => { setBoardSearch(''); setBoardTierFilter(''); setBoardAmbassadorFilter(''); }}
                >
                  Clear filters
                </button>
              )}
            </div>
          </div>

          <ProposalsBoardView
            proposals={boardProposals}
            loading={loading}
            formatCurrency={formatCurrency}
            formatDate={formatDate}
            onView={handleView as unknown as (row: Record<string, unknown>) => void}
            actions={proposalActions as (row: Record<string, unknown>) => React.ReactNode}
            latestEvents={latestEvents}
            supabase={supabase}
            onProposalUpdated={loadProposals}
            columnSorts={boardColumnSorts}
            onColumnSortChange={setBoardColumnSort}
          />
        </>
      )}

      {(listTab === 'hospitality' || listTab === 'community-living') && viewMode === 'metrics' && (
        <>
          <div className="list-toolbar">
            <div className="list-toolbar-left">
              <SearchSelect
                width="inline"
                options={[
                  { value: 'all', label: 'Range: All time' },
                  { value: '90d', label: 'Range: Last 90 days' },
                  { value: '30d', label: 'Range: Last 30 days' },
                ]}
                value={metricsRange}
                onChange={(v) => setMetricsRange(v as '30d' | '90d' | 'all')}
              />
            </div>
          </div>

          <DataTable
            key={`pos-proposals-metrics-${metricsRange}-${listTab}`}
            data={metricsData.rows}
            idKey="id"
            columns={[
              { key: 'name', label: 'Created by', sortable: true, render: (r: { name: string }) => r.name },
              { key: 'sent', label: 'Sent', sortable: true, align: 'center', render: (r: { sent: number }) => r.sent },
              { key: 'accepted', label: 'Accepted', sortable: true, align: 'center', render: (r: { accepted: number }) => r.accepted },
              { key: 'winRate', label: 'Win rate', sortable: true, align: 'right', render: (r: { sent: number; winRate: number }) => r.sent > 0 ? `${r.winRate.toFixed(0)}%` : '—' },
            ]}
            loading={loading}
            searchKeys={['name']}
            searchPlaceholder="Search creator..."
            emptyMessage="No proposals sent in this range yet."
            defaultSort={{ key: 'sent', direction: 'desc' }}
            onRowClick={(r: { name: string }) => { setMetricsCreatorFilter(r.name); setViewMode('list'); }}
            summaryCards={!isAmbassador && metricsData.rows.length > 0 ? [
              { value: metricsData.totals.sent, label: 'Sent (total)' },
              { value: metricsData.totals.accepted, label: 'Accepted (total)' },
              { value: metricsData.totals.sent > 0 ? `${metricsData.totals.winRate.toFixed(0)}%` : '—', label: 'Win rate (overall)' },
            ] : []}
          />
        </>
      )}

      {(listTab === 'hospitality' || listTab === 'community-living') && viewMode === 'list' && <DataTable
        key={`pos-proposals-list-${metricsCreatorFilter}`}
        data={listData}
        columns={columns}
        loading={loading}
        searchKeys={['reference_code', 'property_name', 'contact_name', 'contact_email', 'ambassador_name', '_groupSearch']}
        filters={[
          STATUS_FILTER,
          followUpFilter,
          followedUpFilter,
          ...(!isAmbassador && ambassadorFilter.options.length > 0 ? [ambassadorFilter] : []),
          ...(!isAmbassador && creatorFilter.options.length > 0 ? [creatorFilter] : []),
        ]}
        defaultFilterValues={metricsCreatorFilter ? { ambassador_name: metricsCreatorFilter } : undefined}
        emptyMessage={listTab === 'community-living' ? 'No community living proposals yet.' : 'No hospitality proposals yet. Create your first proposal to get started.'}
        defaultSort={{ key: 'created_at', direction: 'desc' }}
        actions={(row: Row) => row._isGroup ? groupActions(row._group as Row) : proposalActions(row)}
        rowClassName={(row: Row) => row._isGroup ? 'pos-group-row' : (row.is_system_lead ? 'pos-proposal-row-system' : '')}
        renderSubRow={renderGroupSubRow}
        onRowClick={(row: Row) => { if (row._isGroup) toggleGroupRow(row.id as string); }}
        onSearchChange={setListSearch}
      />}

      {listTab === 'group' && <DataTable
        data={groupProposals}
        columns={[
          { key: 'reference_code', label: 'Reference', sortable: true, render: (row: Row) => <code className="text-sm">{row.reference_code}</code> },
          { key: 'group_name', label: 'Group', sortable: true, render: (row: Row) => <div className="fw-500">{row.group_name}</div> },
          { key: 'contact_name', label: 'Contact', sortable: true, render: (row: Row) => <div>{row.contact_name}</div> },
          { key: 'property_count', label: 'Properties', sortable: true, render: (row: Row) => row.property_count || 0 },
          { key: 'assigned_ambassador_name', label: 'Ambassador', sortable: true, render: (row: Row) => row.assigned_ambassador_name || <span className="text-muted">No Ambassador</span> },
          { key: 'annual_total', label: 'Annual Total', sortable: true, render: (row: Row) => formatCurrency(row.annual_total) },
          { key: 'status', label: 'Status', sortable: true, render: (row: Row) => { const cfg = (STATUS_CONFIG[row.status] || STATUS_CONFIG.draft)!; return <StatusBadge bg={cfg.bg} color={cfg.color}>{cfg.icon} {cfg.label}</StatusBadge>; } },
          { key: 'created_at', label: 'Created', sortable: true, render: (row: Row) => formatDate(row.created_at) },
        ]}
        loading={groupsLoading}
        searchKeys={['reference_code', 'group_name', 'contact_name']}
        filters={[STATUS_FILTER]}
        emptyMessage="No group proposals yet."
        defaultSort={{ key: 'created_at', direction: 'desc' }}
        actions={groupActions}
      />}

      {/* ====== CREATE / EDIT MODAL ====== */}
      {showModal && (
        <div className="modal-overlay" onClick={requestCloseModal}>
          <div
            className="modal modal-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>{editingId ? `Edit Proposal` : 'New POS Proposal'}</h2>
              <button className="modal-close" onClick={requestCloseModal}>&times;</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {draftRestored && (
                  <div className="alert-info pos-proposal-draft-banner">
                    <span>📝 Unsaved changes from your previous session were restored.</span>
                    <button
                      type="button"
                      onClick={() => {
                        if (confirm('Discard the restored draft and reload the original?')) {
                          discardDraftBanner();
                          // Re-open to reload fresh data
                          if (editingId) {
                            const idToReopen = editingId;
                            setShowModal(false);
                            setTimeout(() => handleOpenEdit({ id: idToReopen }), 0);
                          } else {
                            setShowModal(false);
                            setTimeout(() => handleOpenCreate(), 0);
                          }
                        }
                      }}
                      className="pos-proposal-draft-discard"
                    >
                      Discard
                    </button>
                  </div>
                )}
                {modalError && (
                  <div className="alert-error pos-proposal-alert">
                    {modalError}
                  </div>
                )}

                {/* Category & Audience */}
                <div className="form-section">
                  <h3 className="modal-section-heading">Proposal Type</h3>
                  <div className="modal-grid-2">
                    <div>
                      <label className="form-label">Category</label>
                      <select
                        className="form-input"
                        value={form.proposal_category || 'hospitality'}
                        onChange={(e) => {
                          const cat = e.target.value;
                          setForm(f => ({ ...f, proposal_category: cat, proposal_audience: '' }));
                        }}
                      >
                        {getCategoryOptions().map(o => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="form-label">Audience</label>
                      <select
                        className="form-input"
                        value={form.proposal_audience || ''}
                        onChange={(e) => {
                          const aud = e.target.value;
                          setForm(f => ({ ...f, proposal_audience: aud }));
                          if (aud) {
                            const snippet = getSnippet(form.proposal_category || 'hospitality', aud);
                            if (snippet) {
                              const hasContent = form.intro_paragraph?.trim() || form.ambassador_quote?.trim();
                              if (!hasContent || confirm('Replace text fields with audience template?')) {
                                setForm(f => ({
                                  ...f,
                                  proposal_audience: aud,
                                  intro_paragraph: snippet.intro,
                                  ambassador_quote: snippet.ambassador_quote || f.ambassador_quote,
                                }));
                              }
                            }
                          }
                        }}
                      >
                        <option value="">Select audience...</option>
                        {getAudiences(form.proposal_category || 'hospitality').map(o => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Property Details */}
                <div className="form-section">
                  <h3 className="modal-section-heading">Property Details</h3>
                  <div className="modal-grid-2">
                    <div>
                      <label className="form-label">Property Name *</label>
                      <input
                        className="form-input"
                        required
                        value={form.property_name}
                        onChange={(e) => setForm(f => ({ ...f, property_name: e.target.value }))}
                        placeholder="e.g. The Grand Hotel"
                      />
                    </div>
                    <div>
                      <label className="form-label">Property Type</label>
                      <select
                        className="form-input"
                        value={form.property_type}
                        onChange={(e) => setForm(f => ({ ...f, property_type: e.target.value }))}
                      >
                        <option value="">Select type...</option>
                        {PROPERTY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="form-label">City</label>
                      <input
                        className="form-input"
                        value={form.property_city}
                        onChange={(e) => setForm(f => ({ ...f, property_city: e.target.value }))}
                        placeholder="e.g. Cape Town"
                      />
                    </div>
                    <div className="pos-proposal-grid-span-2">
                      <label className="form-label">Province / Region</label>
                      <input
                        className="form-input"
                        value={form.property_province}
                        onChange={(e) => setForm(f => ({ ...f, property_province: e.target.value }))}
                        placeholder="e.g. Western Cape"
                      />
                    </div>
                  </div>
                </div>

                {/* Contact */}
                <div className="form-section">
                  <h3 className="modal-section-heading">Contact Person</h3>
                  <div className="modal-grid-2">
                    <div className="pos-proposal-grid-span-2">
                      <label className="form-label">Contact Name</label>
                      <input
                        className="form-input"
                        value={form.contact_name}
                        onChange={(e) => setForm(f => ({ ...f, contact_name: e.target.value }))}
                        placeholder="e.g. John Smith"
                      />
                    </div>
                    <div>
                      <label className="form-label">Email</label>
                      <input
                        className="form-input"
                        type="email"
                        value={form.contact_email}
                        onChange={(e) => setForm(f => ({ ...f, contact_email: e.target.value }))}
                        placeholder="john@hotel.com"
                      />
                    </div>
                    <div>
                      <label className="form-label">Phone</label>
                      <input
                        className="form-input"
                        value={form.contact_phone}
                        onChange={(e) => setForm(f => ({ ...f, contact_phone: e.target.value }))}
                        placeholder="+27 ..."
                      />
                    </div>
                  </div>
                </div>

                {/* Personalisation — before pricing so user sets context first */}
                <div className="form-section">
                  <h3 className="modal-section-heading">Custom Introduction (optional)</h3>
                  <textarea
                    className="form-input pos-proposal-textarea"
                    rows={3}
                    value={form.intro_paragraph}
                    onChange={(e) => setForm(f => ({ ...f, intro_paragraph: e.target.value }))}
                    placeholder="Write a personal, property-specific opening that will appear at the start of the proposal introduction..."
                  />
                  <div className="pos-proposal-field-hint">
                    Appears as the opening paragraph in the proposal. Leave blank for standard copy.
                  </div>
                </div>

                {/* Tier — moved above items so the staff member picks the certification level before adding scope */}
                <div className="form-section">
                  <h3 className="modal-section-heading">Certification Tier</h3>
                  <div className="pos-proposal-section-hint">
                    Select the customer&apos;s preferred service frequency.
                  </div>
                  <div className="modal-grid-3">
                    {['assured', 'distinguished', 'signature'].map(tier => (
                      <label
                        key={tier}
                        className={`pos-tier-card${form.selected_tier === tier ? ' selected' : ''}`}
                      >
                        <input
                          type="radio"
                          name="tier"
                          value={tier}
                          checked={form.selected_tier === tier}
                          onChange={(e) => setForm(f => ({ ...f, selected_tier: e.target.value }))}
                          className="sr-only"
                        />
                        <span className="pos-tier-card-name">{TIER_LABELS[tier]}</span>
                        <span className="text-sm text-muted">
                          {tier === 'assured' ? '1x/year' : tier === 'distinguished' ? '2x/year' : '3x/year'}
                        </span>
                        {tier !== 'assured' && (
                          <span className="pos-tier-card-discount">
                            {tier === 'distinguished' ? '10% discount' : '20% discount'}
                          </span>
                        )}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Items (replaces Service Scope) — hidden when room mode is on */}
                {!form.use_rooms && (
                  <div className="form-section">
                    <h3 className="modal-section-heading">Service Items</h3>
                    <div className="pos-proposal-section-hint">
                      Select items from the catalog. Prices are editable per proposal.
                    </div>
                    <ItemSelector
                      selectedItems={form.items}
                      onChange={(items) => setForm(f => ({ ...f, items }))}
                    />
                  </div>
                )}

                {/* Room Breakdown (optional presentation grouping) */}
                <div className="form-section">
                  <label className="pos-proposal-room-toggle">
                    <input
                      type="checkbox"
                      checked={form.use_rooms}
                      onChange={(e) => {
                        const useRooms = e.target.checked;
                        setForm(f => {
                          if (useRooms) {
                            // Flat → Room: move current items into first room
                            const firstRoom: RoomEntry = { name: '', quantity: 1, items: f.items.length > 0 ? [...f.items] : [] };
                            return { ...f, use_rooms: true, rooms: [firstRoom], items: [] as SelectedItem[] };
                          } else {
                            // Room → Flat: merge all room items back
                            const merged = f.rooms.reduce<SelectedItem[]>((acc, r) => [...acc, ...(r.items || [])], []);
                            return { ...f, use_rooms: false, items: merged, rooms: [] as RoomEntry[] };
                          }
                        });
                      }}
                    />
                    Room-by-room breakdown
                  </label>

                  {form.use_rooms && (
                    <div className="pos-proposal-rooms-wrap">
                      <div className="pos-proposal-section-hint">
                        Each room gets its own items. Pricing is calculated from all items across all rooms.
                      </div>
                      {form.rooms.map((room, idx) => (
                        <div key={idx} className="pos-proposal-room-card">
                          <div className="pos-proposal-room-head">
                            <span className="pos-proposal-room-title">Room {idx + 1}</span>
                            <div className="pos-proposal-room-moves">
                              <button type="button" onClick={() => moveRoom(idx, 'up')} disabled={idx === 0} className="pos-proposal-room-move">&#9650;</button>
                              <button type="button" onClick={() => moveRoom(idx, 'down')} disabled={idx === form.rooms.length - 1} className="pos-proposal-room-move">&#9660;</button>
                              <button type="button" onClick={() => removeRoom(idx)} className="btn-link-danger fs-sm">Remove</button>
                            </div>
                          </div>
                          <div className="modal-grid-2 pos-proposal-room-grid">
                            <div>
                              <label className="form-label">Room / Suite Name</label>
                              <input
                                className="form-input"
                                value={room.name}
                                onChange={(e) => updateRoom(idx, 'name', e.target.value)}
                                placeholder="e.g. Presidential Suite"
                              />
                            </div>
                            <div>
                              <label className="form-label">Quantity</label>
                              <input className="form-input" type="number" min="1" value={room.quantity} onChange={(e) => updateRoom(idx, 'quantity', e.target.value)} />
                            </div>
                          </div>
                          <ItemSelector
                            selectedItems={room.items || []}
                            onChange={(items) => updateRoom(idx, 'items', items)}
                          />
                        </div>
                      ))}
                      <button type="button" className="btn btn-sm btn-secondary pos-proposal-add-room" onClick={addRoom}>
                        + Add Room
                      </button>
                    </div>
                  )}
                </div>

                {/* Scope Notes */}
                <div className="form-section">
                  <h3 className="modal-section-heading">Scope Notes (optional)</h3>
                  <textarea
                    className="form-input pos-proposal-textarea"
                    rows={2}
                    value={form.scope_notes}
                    onChange={(e) => setForm(f => ({ ...f, scope_notes: e.target.value }))}
                    placeholder="e.g. Where two single beds share a room, these are serviced as one large sleep surface."
                  />
                  <div className="pos-proposal-field-hint">
                    One note per line. Displayed below the scope table on the proposal.
                  </div>
                </div>

                {/* Outside service area, excludes travel and accommodation */}
                <div className="form-section">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={form.excludes_travel}
                      onChange={(e) => setForm(f => ({ ...f, excludes_travel: e.target.checked }))}
                    />
                    Property outside service area: pricing excludes travel and accommodation
                  </label>
                  <span className="form-hint">
                    Adds a note under the price: travel and accommodation are excluded and quoted per property once routing and scheduling are confirmed. Does not change the quoted price.
                  </span>
                </div>

                {/* Volume Discount Override — admins + territory managers only */}
                {canOverrideDiscount && (
                  <div className="form-section">
                    <h3 className="modal-section-heading">Volume Discount Override (optional)</h3>
                    <div className="pos-proposal-discount-row">
                      <input
                        type="number"
                        className="form-input pos-proposal-discount-input"
                        min={0}
                        max={100}
                        step="0.5"
                        placeholder="Auto (from volume tier)"
                        value={form.volume_discount_pct_override}
                        onChange={(e) => setForm(f => ({ ...f, volume_discount_pct_override: e.target.value, volume_discount_amt_override: '' }))}
                      />
                      <span className="pos-proposal-discount-unit">%</span>
                      {form.volume_discount_pct_override !== '' && (
                        <button
                          type="button"
                          onClick={() => setForm(f => ({ ...f, volume_discount_pct_override: '' }))}
                          className="pos-proposal-discount-clear"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                    <div className="flex items-center gap-sm mt-md">
                      <span className="pos-proposal-discount-unit-r">R</span>
                      <input
                        type="number"
                        className="form-input"
                        min={0}
                        step="50"
                        placeholder="or a fixed per-visit amount"
                        value={form.volume_discount_amt_override}
                        onChange={(e) => setForm(f => ({ ...f, volume_discount_amt_override: e.target.value, volume_discount_pct_override: '' }))}
                      />
                      {form.volume_discount_amt_override !== '' && (
                        <button
                          type="button"
                          onClick={() => setForm(f => ({ ...f, volume_discount_amt_override: '' }))}
                          className="pos-proposal-discount-clear"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                    <div className="text-sm text-light mt-xs">
                      Leave both empty to use the auto-calculated tier percent. Set a percentage, OR a fixed per-visit Rand amount (incl. VAT) for a sweetener like items thrown in. The Rand amount takes precedence.
                    </div>
                  </div>
                )}

                {/* Images */}
                <div className="form-section">
                  <h3 className="modal-section-heading">Images (optional)</h3>
                  <ImageUpload
                    propertyId={`proposals/${uploadId}`}
                    path="hero"
                    currentUrl={form.hero_image_url}
                    onUploaded={(url) => setForm(f => ({ ...f, hero_image_url: url }))}
                    supabase={supabase}
                    label="Hero Image (property photo for proposal header)"
                  />
                  <ImageUpload
                    propertyId={`proposals/${uploadId}`}
                    path="logo"
                    currentUrl={form.logo_url}
                    onUploaded={(url) => setForm(f => ({ ...f, logo_url: url }))}
                    supabase={supabase}
                    label="Property Logo"
                  />
                  <ImageUpload
                    propertyId={`proposals/${uploadId}`}
                    path="ambassador"
                    currentUrl={form.ambassador_photo_url}
                    onUploaded={(url) => setForm(f => ({ ...f, ambassador_photo_url: url }))}
                    supabase={supabase}
                    label="Ambassador Photo"
                  />
                </div>

                {/* Service Ambassador Details */}
                <div className="form-section">
                  <h3 className="modal-section-heading">Service Ambassador</h3>
                  <div className="pos-proposal-sm-hint">
                    Defaults to your profile. Override below if this proposal is on behalf of another ambassador.
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Name *</label>
                      <input
                        className="form-input"
                        value={form.ambassador_name}
                        onChange={(e) => setForm(f => ({ ...f, ambassador_name: e.target.value }))}
                        placeholder="Full name"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Title</label>
                      <input
                        className="form-input"
                        value={form.ambassador_title}
                        onChange={(e) => setForm(f => ({ ...f, ambassador_title: e.target.value }))}
                        placeholder="Peace of Sleep™ Service Ambassador"
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Email</label>
                      <input
                        className="form-input"
                        type="email"
                        value={form.ambassador_email}
                        onChange={(e) => setForm(f => ({ ...f, ambassador_email: e.target.value }))}
                        placeholder="email@example.com"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Phone / WhatsApp</label>
                      <input
                        className="form-input"
                        value={form.ambassador_phone}
                        onChange={(e) => setForm(f => ({ ...f, ambassador_phone: e.target.value }))}
                        placeholder="+27..."
                      />
                    </div>
                  </div>
                  <div className="form-group pos-proposal-group-gap">
                    <label className="form-label">Personal Quote</label>
                    <textarea
                      className="form-input pos-proposal-textarea"
                      rows={2}
                      value={form.ambassador_quote}
                      onChange={(e) => setForm(f => ({ ...f, ambassador_quote: e.target.value }))}
                      placeholder="If you have any questions about this proposal..."
                    />
                  </div>
                </div>

                {/* Internal Note */}
                <div className="form-section">
                  <h3 className="modal-section-heading">Internal Note (optional)</h3>
                  <textarea
                    className="form-input pos-proposal-textarea"
                    rows={3}
                    value={form.personal_note}
                    onChange={(e) => setForm(f => ({ ...f, personal_note: e.target.value }))}
                    placeholder="Internal note for the team — not shown on the proposal..."
                  />
                </div>

                {/* Attribution: who sourced and who manages this property. Carries
                    to the property on conversion. Admin-only. */}
                {isAdminUser && (
                  <div className="form-section">
                    <h3 className="modal-section-heading">Attribution</h3>
                    <div className="text-sm text-muted mb-3">
                      Link the POS Ambassador who sourced this opportunity and, if known, the assigned manager. These carry through to the property when the proposal is converted.
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">POS Ambassador</label>
                        <SearchSelect
                          disabled={!canSetAmbassador}
                          options={[{ value: '', label: 'No Ambassador' }, ...ambassadorOptions.map((o) => ({ value: o.id, label: o.name }))]}
                          value={form.assigned_ambassador_id}
                          onChange={(v) => setForm(f => ({ ...f, assigned_ambassador_id: v }))}
                        />
                        {!canSetAmbassador && (
                          <div className="text-sm text-muted mt-1">Ambassador attribution is a payout-admin function.</div>
                        )}
                      </div>
                      <div className="form-group">
                        <label className="form-label">Manager (optional)</label>
                        <SearchSelect
                          options={[{ value: '', label: 'No manager' }, ...managerOptions.map((o) => ({ value: o.id, label: o.name }))]}
                          value={form.assigned_manager_id}
                          onChange={(v) => setForm(f => ({ ...f, assigned_manager_id: v }))}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="modal-footer pos-proposal-footer-split">
                <div>
                  {editingId && (
                    <button
                      type="button"
                      onClick={handleDelete}
                      disabled={modalLoading}
                      className="pos-proposal-delete-link"
                    >
                      Delete Proposal
                    </button>
                  )}
                </div>
                <div className="pos-proposal-footer-actions">
                  <button type="button" className="btn btn-secondary" onClick={requestCloseModal}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={modalLoading}>
                    {modalLoading ? (editingId ? 'Saving...' : 'Creating...') : (editingId ? 'Save Changes' : 'Create Proposal')}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ====== GROUP CREATE / EDIT MODAL ====== */}
      {showGroupModal && (
        <div className="modal-overlay" onClick={requestCloseGroupModal}>
          <div className="modal modal-md" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingGroupId ? 'Edit Group Proposal' : 'New Group Proposal'}</h2>
              <button className="modal-close" onClick={requestCloseGroupModal}>&times;</button>
            </div>
            <form onSubmit={handleGroupSubmit}>
              <div className="modal-body">
                {groupDraftRestored && (
                  <div className="pos-proposal-draft-banner">
                    <span>📝 Unsaved changes from your previous session were restored.</span>
                    <button
                      type="button"
                      onClick={() => {
                        if (confirm('Discard the restored draft and reload the original?')) {
                          discardGroupDraftBanner();
                          if (editingGroupId) {
                            const idToReopen = editingGroupId;
                            setShowGroupModal(false);
                            setTimeout(() => handleOpenGroupEdit({ id: idToReopen }), 0);
                          } else {
                            setShowGroupModal(false);
                            setTimeout(() => handleOpenGroupCreate(), 0);
                          }
                        }
                      }}
                      className="pos-proposal-draft-discard"
                    >
                      Discard
                    </button>
                  </div>
                )}
                {groupModalError && (
                  <div className="alert-error pos-proposal-alert">
                    {groupModalError}
                  </div>
                )}

                {groupPrefillMembers.length > 0 && (
                  <div className="alert alert-info mb-md">
                    From a Leads collection. Add these {groupPrefillMembers.length} properties to the group: {groupPrefillMembers.join(', ')}.
                  </div>
                )}

                {/* Group Details */}
                <div className="form-section">
                  <h3 className="modal-section-heading">Group Details</h3>
                  <div className="modal-grid-2">
                    <div>
                      <label className="form-label">Group Name *</label>
                      <input className="form-input" required value={groupForm.group_name} onChange={e => setGroupForm(f => ({ ...f, group_name: e.target.value }))} placeholder="e.g. Tintswalo Collection" autoFocus />
                    </div>
                  </div>
                </div>

                {/* Contact */}
                <div className="form-section">
                  <h3 className="modal-section-heading">Contact Person</h3>
                  <div className="modal-grid-2">
                    <div className="pos-proposal-grid-span-2">
                      <label className="form-label">Contact Name *</label>
                      <input className="form-input" required value={groupForm.contact_name} onChange={e => setGroupForm(f => ({ ...f, contact_name: e.target.value }))} placeholder="e.g. Jane Doe" />
                    </div>
                    <div>
                      <label className="form-label">Email</label>
                      <input className="form-input" type="email" value={groupForm.contact_email} onChange={e => setGroupForm(f => ({ ...f, contact_email: e.target.value }))} placeholder="name@example.com" />
                    </div>
                    <div>
                      <label className="form-label">Phone</label>
                      <input className="form-input" value={groupForm.contact_phone} onChange={e => setGroupForm(f => ({ ...f, contact_phone: e.target.value }))} placeholder="+27 ..." />
                    </div>
                  </div>
                </div>

                {/* Custom Introduction — before properties/pricing for natural flow */}
                <div className="form-section">
                  <h3 className="modal-section-heading">Custom Introduction (optional)</h3>
                  <textarea className="form-input pos-proposal-textarea" rows={3} value={groupForm.intro_paragraph} onChange={e => setGroupForm(f => ({ ...f, intro_paragraph: e.target.value }))} placeholder="Write a personal, group-specific opening..." />
                  <div className="pos-proposal-field-hint">Appears as the opening paragraph in the proposal. Leave blank for standard copy.</div>
                </div>

                {/* Outside service area, excludes travel and accommodation */}
                <div className="form-section">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={groupForm.excludes_travel}
                      onChange={e => setGroupForm(f => ({ ...f, excludes_travel: e.target.checked }))}
                    />
                    Properties outside service area: pricing excludes travel and accommodation
                  </label>
                  <span className="form-hint">
                    Adds a note under the price: travel and accommodation are excluded and quoted per property once routing and scheduling are confirmed. Does not change the quoted price.
                  </span>
                </div>

                {/* Tier — moved above the properties table so the level is set before adding properties */}
                <div className="form-section">
                  <h3 className="modal-section-heading">Certification Tier</h3>
                  <div className="pos-proposal-section-hint">
                    Select the customer&apos;s preferred service frequency.
                  </div>
                  <div className="modal-grid-3">
                    {['assured', 'distinguished', 'signature'].map(tier => (
                      <label key={tier} className={`pos-tier-card${groupForm.selected_tier === tier ? ' selected' : ''}`}>
                        <input type="radio" name="group-tier" value={tier} checked={groupForm.selected_tier === tier} onChange={e => setGroupForm(f => ({ ...f, selected_tier: e.target.value }))} className="sr-only" />
                        <span className="pos-tier-card-name">{TIER_LABELS[tier]}</span>
                        <span className="text-sm text-muted">{tier === 'assured' ? '1x/year' : tier === 'distinguished' ? '2x/year' : '3x/year'}</span>
                        {tier !== 'assured' && <span className="pos-tier-card-discount">{tier === 'distinguished' ? '10% discount' : '20% discount'}</span>}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Properties Summary Table (only when editing) */}
                {editingGroupId && (
                  <div className="form-section">
                    <h3 className="modal-section-heading">
                      Properties ({groupProperties.length})
                    </h3>
                    {groupProperties.length > 0 ? (
                      <div className="pos-proposal-table-wrap">
                        <table className="pos-proposal-table">
                          <thead>
                            <tr className="pos-proposal-table-head-row">
                              <th className="pos-proposal-th-left">Property</th>
                              <th className="pos-proposal-th-center">Items</th>
                              <th className="pos-proposal-th-right">Per Visit</th>
                              <th className="pos-proposal-th-right">Annual</th>
                              <th className="pos-proposal-th-spacer"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {groupProperties.map(p => {
                              const scope: ProposalDataScope = p.proposal_data?.scope || {};
                              const itemCount = scope.total_items || ((p.beds_small || 0) + (p.beds_large || 0) + (p.toppers_small || 0) + (p.toppers_large || 0));
                              return (
                                <tr key={p.id} className="pos-proposal-tr">
                                  <td className="pos-proposal-td-name">
                                    {p.property_name}
                                    {p.property_city && <span className="pos-proposal-td-name-city">— {p.property_city}</span>}
                                  </td>
                                  <td className="pos-proposal-td-center">{itemCount}</td>
                                  <td className="pos-proposal-td-right">{formatCurrency(p.total_per_visit)}</td>
                                  <td className="pos-proposal-td-right">{formatCurrency(p.annual_total)}</td>
                                  <td className="pos-proposal-td-actions">
                              <span className="action-icon" onClick={() => handleEditGroupProperty(p)} title="Edit property">✏️</span>
                              <span className="action-icon action-icon-error" onClick={() => handleRemoveGroupProperty(p.id)} title="Remove">✕</span>
                            </td>
                                </tr>
                              );
                            })}
                          </tbody>
                          {groupProperties.length > 1 && (
                            <tfoot>
                              <tr className="pos-proposal-tr-total">
                                <td className="pos-proposal-td-total">Total</td>
                                <td className="pos-proposal-td-center">{groupProperties.reduce((s, p) => s + ((p.proposal_data?.scope?.total_items) || ((p.beds_small || 0) + (p.beds_large || 0) + (p.toppers_small || 0) + (p.toppers_large || 0))), 0)}</td>
                                <td className="pos-proposal-td-right">{formatCurrency(groupProperties.reduce((s, p) => s + (p.total_per_visit || 0), 0))}</td>
                                <td className="pos-proposal-td-right">{formatCurrency(groupProperties.reduce((s, p) => s + (p.annual_total || 0), 0))}</td>
                                <td></td>
                              </tr>
                            </tfoot>
                          )}
                        </table>
                      </div>
                    ) : (
                      <p className="pos-proposal-empty-note">No properties yet. Save the group first, then add properties.</p>
                    )}
                    <div className="pos-proposal-table-actions">
                      <button type="button" className="btn btn-sm btn-secondary" onClick={handleOpenPropertySubModal}>
                        + Add New
                      </button>
                      {proposals.filter(p => !p.group_proposal_id).length > 0 && (
                        <button type="button" className="btn btn-sm btn-secondary pos-proposal-link-existing" onClick={() => { setLinkPickerSearch(''); setShowLinkPicker(true); }}>
                          + Link Existing
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Images */}
                <div className="form-section">
                  <h3 className="modal-section-heading">Images (optional)</h3>
                  <ImageUpload propertyId={`proposals/${groupUploadId}`} path="hero" currentUrl={groupForm.hero_image_url} onUploaded={(url) => setGroupForm(f => ({ ...f, hero_image_url: url }))} supabase={supabase} label="Hero Image" />
                  <ImageUpload propertyId={`proposals/${groupUploadId}`} path="logo" currentUrl={groupForm.logo_url} onUploaded={(url) => setGroupForm(f => ({ ...f, logo_url: url }))} supabase={supabase} label="Group Logo" />
                  <ImageUpload propertyId={`proposals/${groupUploadId}`} path="ambassador" currentUrl={groupForm.ambassador_photo_url} onUploaded={(url) => setGroupForm(f => ({ ...f, ambassador_photo_url: url }))} supabase={supabase} label="Ambassador Photo" />
                </div>

                {/* Ambassador */}
                <div className="form-section">
                  <h3 className="modal-section-heading">Service Ambassador</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Name *</label>
                      <input className="form-input" value={groupForm.ambassador_name} onChange={e => setGroupForm(f => ({ ...f, ambassador_name: e.target.value }))} placeholder="Full name" required />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Title</label>
                      <input className="form-input" value={groupForm.ambassador_title} onChange={e => setGroupForm(f => ({ ...f, ambassador_title: e.target.value }))} />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Email</label>
                      <input className="form-input" type="email" value={groupForm.ambassador_email} onChange={e => setGroupForm(f => ({ ...f, ambassador_email: e.target.value }))} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Phone / WhatsApp</label>
                      <input className="form-input" value={groupForm.ambassador_phone} onChange={e => setGroupForm(f => ({ ...f, ambassador_phone: e.target.value }))} />
                    </div>
                  </div>
                  <div className="form-group pos-proposal-group-gap">
                    <label className="form-label">Personal Quote</label>
                    <textarea className="form-input pos-proposal-textarea" rows={2} value={groupForm.ambassador_quote} onChange={e => setGroupForm(f => ({ ...f, ambassador_quote: e.target.value }))} />
                  </div>
                </div>

                {/* Internal Note */}
                <div className="form-section">
                  <h3 className="modal-section-heading">Internal Note (optional)</h3>
                  <textarea className="form-input pos-proposal-textarea" rows={3} value={groupForm.personal_note} onChange={e => setGroupForm(f => ({ ...f, personal_note: e.target.value }))} placeholder="Internal note for the team..." />
                </div>

                {/* Attribution: the POS Ambassador who closed the group. Applies to
                    every property the group converts to. Managers are per property,
                    set on each property, not here. Admin-only. */}
                {isAdminUser && (
                  <div className="form-section">
                    <h3 className="modal-section-heading">Attribution</h3>
                    <div className="text-sm text-muted mb-3">
                      Link the POS Ambassador who closed this group. It applies to every property in the group and carries through when each one is converted. Managers are assigned per property, not here.
                    </div>
                    <div className="form-group">
                      <label className="form-label">POS Ambassador</label>
                      <SearchSelect
                        disabled={!canSetAmbassador}
                        options={[{ value: '', label: 'No Ambassador' }, ...ambassadorOptions.map((o) => ({ value: o.id, label: o.name }))]}
                        value={groupForm.assigned_ambassador_id || null}
                        onChange={v => setGroupForm(f => ({ ...f, assigned_ambassador_id: v }))}
                      />
                      {!canSetAmbassador && (
                        <div className="text-sm text-muted mt-1">Ambassador attribution is a payout-admin function.</div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="modal-footer pos-proposal-footer-split">
                <div>
                  {editingGroupId && (
                    <button type="button" onClick={handleDeleteGroup} disabled={groupModalLoading} className="btn-link-danger fs-sm">
                      Delete Group
                    </button>
                  )}
                </div>
                <div className="pos-proposal-footer-actions">
                  {editingGroupId && groupProperties.length > 0 && (
                    <button type="button" className="btn btn-secondary" onClick={handleSendGroup} disabled={groupModalLoading}>
                      Send Proposal
                    </button>
                  )}
                  <button type="button" className="btn btn-secondary" onClick={requestCloseGroupModal}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={groupModalLoading}>
                    {groupModalLoading ? 'Saving...' : (editingGroupId ? 'Save Changes' : 'Create Group')}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ====== PROPERTY SUB-MODAL (Add to Group) ====== */}
      {showPropertySubModal && (
        <div className="modal-overlay pos-proposal-overlay-1001" onClick={() => setShowPropertySubModal(false)}>
          <div className="modal modal-md" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingPropertyId ? 'Edit Property' : 'Add Property to Group'}</h2>
              <button className="modal-close" onClick={() => setShowPropertySubModal(false)}>&times;</button>
            </div>
            <form onSubmit={handlePropertySubSubmit}>
              <div className="modal-body">
                {propertySubError && (
                  <div className="alert-error pos-proposal-alert">{propertySubError}</div>
                )}

                {/* Property Details */}
                <div className="form-section">
                  <h3 className="modal-section-heading">Property Details</h3>
                  <div className="modal-grid-2">
                    <div className="pos-proposal-grid-span-2">
                      <label className="form-label">Property Name *</label>
                      <input className="form-input" required value={propertySubForm.property_name} onChange={e => setPropertySubForm(f => ({ ...f, property_name: e.target.value }))} placeholder="e.g. The Grand Hotel" autoFocus />
                    </div>
                    <div>
                      <label className="form-label">Property Type</label>
                      <select className="form-input" value={propertySubForm.property_type} onChange={e => setPropertySubForm(f => ({ ...f, property_type: e.target.value }))}>
                        <option value="">Select type...</option>
                        {PROPERTY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="form-label">City</label>
                      <input className="form-input" value={propertySubForm.property_city} onChange={e => setPropertySubForm(f => ({ ...f, property_city: e.target.value }))} placeholder="e.g. Cape Town" />
                    </div>
                    <div className="pos-proposal-grid-span-2">
                      <label className="form-label">Province / Region</label>
                      <input className="form-input" value={propertySubForm.property_province} onChange={e => setPropertySubForm(f => ({ ...f, property_province: e.target.value }))} placeholder="e.g. Western Cape" />
                    </div>
                  </div>
                </div>

                {/* Contact */}
                <div className="form-section">
                  <h3 className="modal-section-heading">Contact Person</h3>
                  <div className="modal-grid-2">
                    <div className="pos-proposal-grid-span-2">
                      <label className="form-label">Contact Name</label>
                      <input className="form-input" value={propertySubForm.contact_name} onChange={e => setPropertySubForm(f => ({ ...f, contact_name: e.target.value }))} placeholder="e.g. John Smith" />
                    </div>
                    <div>
                      <label className="form-label">Email</label>
                      <input className="form-input" type="email" value={propertySubForm.contact_email} onChange={e => setPropertySubForm(f => ({ ...f, contact_email: e.target.value }))} />
                    </div>
                    <div>
                      <label className="form-label">Phone</label>
                      <input className="form-input" value={propertySubForm.contact_phone} onChange={e => setPropertySubForm(f => ({ ...f, contact_phone: e.target.value }))} />
                    </div>
                  </div>
                </div>

                {/* Items */}
                {!propertySubForm.use_rooms && (
                  <div className="form-section">
                    <h3 className="modal-section-heading">Service Items</h3>
                    <div className="pos-proposal-section-hint">
                      Select items from the catalog. Prices are editable per proposal.
                    </div>
                    <ItemSelector selectedItems={propertySubForm.items} onChange={(items) => setPropertySubForm(f => ({ ...f, items }))} />
                  </div>
                )}

                {/* Room Breakdown */}
                <div className="form-section">
                  <label className="pos-proposal-room-toggle">
                    <input type="checkbox" checked={propertySubForm.use_rooms} onChange={e => {
                      const useRooms = e.target.checked;
                      setPropertySubForm(f => {
                        if (useRooms) {
                          const firstRoom: RoomEntry = { name: '', quantity: 1, items: f.items.length > 0 ? [...f.items] : [] };
                          return { ...f, use_rooms: true, rooms: [firstRoom], items: [] as SelectedItem[] };
                        } else {
                          const merged = f.rooms.reduce<SelectedItem[]>((acc, r) => [...acc, ...(r.items || [])], []);
                          return { ...f, use_rooms: false, items: merged, rooms: [] as RoomEntry[] };
                        }
                      });
                    }} />
                    Room-by-room breakdown
                  </label>
                  {propertySubForm.use_rooms && (
                    <div className="pos-proposal-rooms-wrap">
                      <div className="pos-proposal-section-hint">Each room gets its own items.</div>
                      {propertySubForm.rooms.map((room, idx) => (
                        <div key={idx} className="pos-proposal-room-card">
                          <div className="pos-proposal-room-head">
                            <span className="pos-proposal-room-title">Room {idx + 1}</span>
                            <button type="button" onClick={() => removeSubRoom(idx)} className="btn-link-danger fs-sm">Remove</button>
                          </div>
                          <div className="modal-grid-2 pos-proposal-room-grid">
                            <div><label className="form-label">Room / Suite Name</label><input className="form-input" value={room.name} onChange={e => updateSubRoom(idx, 'name', e.target.value)} placeholder="e.g. Presidential Suite" /></div>
                            <div><label className="form-label">Quantity</label><input className="form-input" type="number" min="1" value={room.quantity} onChange={e => updateSubRoom(idx, 'quantity', e.target.value)} /></div>
                          </div>
                          <ItemSelector selectedItems={room.items || []} onChange={(items) => updateSubRoom(idx, 'items', items)} />
                        </div>
                      ))}
                      <button type="button" className="btn btn-sm btn-secondary pos-proposal-add-room" onClick={addSubRoom}>+ Add Room</button>
                    </div>
                  )}
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowPropertySubModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={propertySubLoading}>
                  {propertySubLoading ? 'Saving...' : (editingPropertyId ? 'Save Changes' : 'Add to Group')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ====== LINK EXISTING PROPERTY PICKER ====== */}
      {showLinkPicker && (
        <div className="modal-overlay pos-proposal-overlay-1002" onClick={() => setShowLinkPicker(false)}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Link Existing Property</h2>
              <button className="modal-close" onClick={() => setShowLinkPicker(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="pos-proposal-section-hint">
                Select a standalone property proposal to add to this group. Its pricing will be recalculated with the group volume rate.
              </div>
              {(() => {
                const available = proposals.filter(p => !p.group_proposal_id);
                if (available.length === 0) {
                  return <p className="text-sm text-muted">No standalone proposals available.</p>;
                }
                const q = linkPickerSearch.trim().toLowerCase();
                const shown = q
                  ? available.filter(p => [p.property_name, p.property_city, p.contact_name]
                      .filter(Boolean)
                      .some(v => String(v).toLowerCase().includes(q)))
                  : available;
                return (
                  <>
                    <input
                      type="text"
                      autoFocus
                      value={linkPickerSearch}
                      onChange={e => setLinkPickerSearch(e.target.value)}
                      placeholder={`Search ${available.length} standalone proposals by name, city or contact`}
                      className="pos-proposal-link-search"
                    />
                    {shown.length === 0 ? (
                      <p className="text-sm text-muted">No proposals match &quot;{linkPickerSearch}&quot;.</p>
                    ) : (
                      <div className="pos-proposal-link-list">
                        {shown.map(p => (
                          <div
                            key={p.id}
                            className="pos-proposal-link-row"
                            onClick={() => handleLinkProperty(p.id)}
                          >
                            <div>
                              <div className="fw-500">{p.property_name}</div>
                              <div className="text-sm text-muted">{[p.property_city, p.contact_name].filter(Boolean).join(' · ')}</div>
                            </div>
                            <div className="text-sm pos-proposal-gold-amount">
                              {formatCurrency(p.annual_total)}/yr
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowLinkPicker(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ====== VIEW GROUP MODAL ====== */}
      {viewGroup && (
        <div className="modal-overlay" onClick={() => setViewGroup(null)}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{viewGroup.reference_code}</h2>
              <button className="modal-close" onClick={() => setViewGroup(null)}>&times;</button>
            </div>
            <div className="modal-body">
              {/* Status */}
              <div className="view-section pos-proposal-view-status">
                {(() => { const cfg = (STATUS_CONFIG[viewGroup.status] || STATUS_CONFIG.draft)!; return <StatusBadge bg={cfg.bg} color={cfg.color}>{cfg.icon} {cfg.label}</StatusBadge>; })()}
                {viewGroup.is_locked && <span className="pos-proposal-locked-tag">🔒 Locked</span>}
              </div>

              {/* Group Name */}
              <div className="view-section">
                <h3 className="section-title">Group</h3>
                <div className="fw-500">{viewGroup.group_name}</div>
              </div>

              {/* Contact */}
              <div className="view-section">
                <h3 className="section-title">Contact</h3>
                <div>{viewGroup.contact_name}</div>
                {viewGroup.contact_email && <div className="text-sm text-muted">{viewGroup.contact_email}</div>}
                {viewGroup.contact_phone && <div className="text-sm text-muted">{viewGroup.contact_phone}</div>}
              </div>

              {/* Team. Managers are assigned per property, not on the group. */}
              <div className="view-section">
                <h3 className="section-title">Team</h3>
                <div className="detail-row"><span className="label">Created by</span><span className="value">{viewGroup.ambassador_name || <span className="text-muted">—</span>}</span></div>
                <div className="detail-row"><span className="label">POS Ambassador</span><span className="value">{viewGroup.assigned_ambassador_name || <span className="text-muted">No Ambassador</span>}</span></div>
              </div>

              {/* Properties Summary */}
              <div className="view-section">
                <h3 className="section-title">Properties ({viewGroupProperties.length})</h3>
                {viewGroupProperties.length > 0 ? (
                  <div className="pos-proposal-detail-block">
                    {viewGroupProperties.map((p, i) => {
                      const scope = p.proposal_data?.scope || {};
                      const itemCount = scope.total_items || ((p.beds_small || 0) + (p.beds_large || 0) + (p.toppers_small || 0) + (p.toppers_large || 0));
                      return (
                        <div key={i} className="detail-row">
                          <span className="label">
                            {p.property_name}
                            {p.property_city && <span className="text-muted"> — {p.property_city}</span>}
                          </span>
                          <span className="value">{itemCount} items · {formatCurrency(p.annual_total)}/yr</span>
                        </div>
                      );
                    })}
                    <div className="detail-row pos-proposal-detail-divider">
                      <span className="label"><strong>Group Total</strong></span>
                      <span className="value"><strong>{formatCurrency(viewGroup.annual_total)}/yr</strong></span>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-muted">No properties</div>
                )}
              </div>

              {/* Pricing */}
              <div className="view-section">
                <h3 className="section-title">Pricing</h3>
                <div className="detail-row"><span className="label">Tier</span><span className="value">{TIER_LABELS[viewGroup.selected_tier]}</span></div>
                <div className="detail-row"><span className="label">Visits/year</span><span className="value">{viewGroup.visits_per_year}</span></div>
                {(viewGroup.volume_discount_pct ?? 0) > 0 && <div className="detail-row"><span className="label">Volume Discount</span><span className="value pos-proposal-discount-value">{viewGroup.volume_discount_pct}%</span></div>}
                <div className="detail-row"><span className="label">Per visit</span><span className="value">{formatCurrency(viewGroup.total_per_visit)}</span></div>
                <div className="detail-row">
                  <span className="label">Annual Total</span>
                  <span className="value pos-proposal-annual-value">{formatCurrency(viewGroup.annual_total)}</span>
                </div>
              </div>

              {/* Timeline */}
              <div className="view-section">
                <h3 className="section-title">Timeline</h3>
                <div className="text-sm">
                  <div className="detail-row"><span className="label">Created</span><span className="value">{formatDate(viewGroup.created_at)}</span></div>
                  {viewGroup.sent_at && <div className="detail-row"><span className="label">Sent</span><span className="value">{formatDate(viewGroup.sent_at)}</span></div>}
                  {viewGroup.viewed_at && <div className="detail-row"><span className="label">First viewed</span><span className="value">{formatDate(viewGroup.viewed_at)}</span></div>}
                  {viewGroup.accepted_at && <div className="detail-row"><span className="label">Accepted</span><span className="value">{formatDate(viewGroup.accepted_at)}</span></div>}
                  {viewGroup.expires_at && <div className="detail-row"><span className="label">Expires</span><span className="value">{formatDate(viewGroup.expires_at)}</span></div>}
                </div>
              </div>

              {/* Activity / Analytics */}
              <div className="view-section">
                <h3 className="section-title">Activity</h3>
                <div className="detail-row"><span className="label">Views</span><span className="value">{viewGroup.view_count || 0}</span></div>
                {eventsLoading ? (
                  <div className="text-sm text-muted pos-proposal-activity-pad">Loading...</div>
                ) : proposalEvents.length === 0 ? (
                  <div className="text-sm text-muted pos-proposal-activity-pad">No detailed activity yet</div>
                ) : (() => {
                  const views = proposalEvents.filter(e => e.event_type === 'view');
                  const scrolls = proposalEvents.filter(e => e.event_type === 'scroll');
                  const times = proposalEvents.filter(e => e.event_type === 'time_on_page');
                  const clicks = proposalEvents.filter(e => e.event_type === 'cta_click');
                  const lastView = views[0];
                  const maxTime = times.length > 0 ? Math.max(...times.map(t => t.event_data?.seconds || 0)) : 0;
                  const sections = [...new Set(scrolls.map(s => s.event_data?.section).filter(Boolean))];
                  const mobileViews = views.filter(v => v.event_data?.is_mobile).length;
                  const desktopViews = views.length - mobileViews;
                  const timeLabel = maxTime >= 180 ? '3min+' : maxTime >= 60 ? `${Math.floor(maxTime / 60)}min ${maxTime % 60}s` : maxTime > 0 ? `${maxTime}s` : null;
                  const timeAgo = (dateStr: string) => {
                    const diff = Date.now() - new Date(dateStr).getTime();
                    const mins = Math.floor(diff / 60000);
                    if (mins < 1) return 'just now';
                    if (mins < 60) return `${mins}m ago`;
                    const hrs = Math.floor(mins / 60);
                    if (hrs < 24) return `${hrs}h ago`;
                    return `${Math.floor(hrs / 24)}d ago`;
                  };
                  return (
                    <>
                      <div className="pos-proposal-activity-summary">
                        <strong>{views.length}</strong> view{views.length !== 1 ? 's' : ''}
                        {lastView && <span className="text-muted"> · last {timeAgo(lastView.created_at)}</span>}
                        {lastView && <span className="text-muted"> · {lastView.event_data?.is_mobile ? 'mobile' : 'desktop'}</span>}
                        {mobileViews > 0 && desktopViews > 0 && <span className="text-muted"> ({desktopViews} desktop, {mobileViews} mobile)</span>}
                        {timeLabel && <><br />Read time: <strong>{timeLabel}</strong></>}
                        {sections.length > 0 && <><br />Scrolled to: {sections.map(s => <span key={s} className="pos-proposal-activity-chip-info">{s}</span>)}</>}
                        {clicks.length > 0 && <><br />Clicked: {[...new Set(clicks.map(c => c.event_data?.target))].filter(Boolean).map(t => <span key={t} className="pos-proposal-activity-chip-success">{t}</span>)}</>}
                      </div>
                      <div className="pos-proposal-activity-list">
                        {proposalEvents.slice(0, 50).map((evt, i) => {
                          const icons: Record<string, string> = { view: '👁', scroll: '📜', time_on_page: '⏱', cta_click: '👆' };
                          const labels: Record<string, string> = {
                            view: `Viewed${evt.event_data?.is_mobile ? ' (mobile)' : ' (desktop)'}`,
                            scroll: `Scrolled to ${evt.event_data?.section || '?'}`,
                            time_on_page: `On page ${evt.event_data?.seconds || 0}s`,
                            cta_click: `Clicked ${evt.event_data?.target || '?'}`,
                          };
                          return (
                            <div key={i} className="pos-proposal-activity-row">
                              <span>{icons[evt.event_type] || '·'}</span>
                              <span className="pos-proposal-activity-row-label">{labels[evt.event_type] || evt.event_type}</span>
                              <span className="text-muted">{timeAgo(evt.created_at)}</span>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  );
                })()}
              </div>

              {/* Version History */}
              {proposalVersions.length > 0 && (
                <div className="view-section">
                  <h3 className="section-title">Version History</h3>
                  <div className="pos-proposal-version-list">
                    {proposalVersions.map((v, i) => (
                      <div key={i} className="pos-proposal-version-row">
                        <span className={`pos-proposal-version-badge${v.is_final ? ' pos-proposal-version-badge-final' : ''}`}>
                          {v.is_final ? '✓' : `v${v.version}`}
                        </span>
                        <span className="pos-proposal-version-label">
                          {v.change_reason || (v.is_final ? 'Agreement signed' : `Version ${v.version}`)}
                          {v.user_name && <span className="text-muted"> — {v.user_name}</span>}
                        </span>
                        <span className="text-muted pos-proposal-version-date">{formatDate(v.changed_at)}</span>
                      </div>
                    ))}
                  </div>
                  {viewGroup.is_locked && (
                    <div className="pos-proposal-locked-banner">
                      🔒 This proposal is permanently locked — signed {formatDate(viewGroup.locked_at)}
                    </div>
                  )}
                  {viewGroup.signed_pdf_url && (
                    <div className="pos-proposal-signed-row">
                      <button type="button" className="btn btn-sm pos-proposal-signed-btn" onClick={() => void openSignedAgreement(viewGroup.signed_pdf_url!, 'Signed Agreement')}>📄 View Signed Agreement</button>
                    </div>
                  )}
                </div>
              )}

              {/* Internal Note */}
              {viewGroup.personal_note && (
                <div className="view-section">
                  <h3 className="section-title">Internal Note</h3>
                  <div className="pos-personal-note">{viewGroup.personal_note}</div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={() => handleCopyLink(viewGroup.reference_code, viewGroup.access_key)}>Copy Link</button>
              {viewGroup.signed_pdf_url ? (
                <button type="button" className="btn btn-success" onClick={() => void openSignedAgreement(viewGroup.signed_pdf_url!, 'Signed Agreement')}>View Signed Agreement</button>
              ) : (
                <button className="btn btn-primary" onClick={() => handleCopyAgreementLink(viewGroup.reference_code, viewGroup.access_key)} disabled={viewGroup.status === 'accepted'}>Agreement Link</button>
              )}
              {viewGroup.agreement_pdf_url && <button type="button" className="btn btn-secondary" onClick={() => void openSignedAgreement(viewGroup.agreement_pdf_url!, 'Agreement')}>📄 Agreement PDF</button>}
              <button className="btn btn-secondary" onClick={() => setViewGroup(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ====== SEND MODAL ====== */}
      {sendingProposal && (
        <ProposalSendModal
          proposal={sendingProposal as unknown as { id: string }}
          supabase={supabase}
          isGroup={groupProposals.some(g => g.id === sendingProposal.id)}
          onClose={() => setSendingProposal(null)}
          onSent={() => { loadProposals(); loadGroupProposals(); }}
        />
      )}

      {/* ====== FOLLOW-UP MODAL ====== */}
      {followUpProposal && (
        <ProposalFollowUpModal
          proposal={{
            id: followUpProposal.id,
            reference_code: followUpProposal.reference_code,
            property_name: followUpProposal.property_name,
            contact_name: followUpProposal.contact_name,
            contact_email: followUpProposal.contact_email,
            contact_phone: followUpProposal.contact_phone as string | undefined,
            viewed_at: (followUpProposal.viewed_at as string | null) ?? null,
          }}
          whatsAppSentAt={followUps.get(followUpProposal.id as string)?.whatsAppAt ?? null}
          supabase={supabase}
          onClose={() => setFollowUpProposal(null)}
          onSent={() => { loadProposals(); }}
        />
      )}

      {/* ====== ASSIGN MODAL ====== */}
      {assigningProposal && (
        <ProposalAssignModal
          proposal={{
            id: assigningProposal.id,
            reference_code: assigningProposal.reference_code,
            property_name: assigningProposal.property_name,
            sender_user_id: assigningProposal.sender_user_id ?? null,
            assigned_manager_id: assigningProposal.assigned_manager_id ?? null,
            is_system_lead: assigningProposal.is_system_lead === true,
            proposal_data: assigningProposal.proposal_data ?? null,
          }}
          supabase={supabase}
          onClose={() => setAssigningProposal(null)}
          onSaved={loadProposals}
        />
      )}

      {/* ====== VIEW MODAL ====== */}
      {viewProposal && (
        <div className="modal-overlay" onClick={() => setViewProposal(null)}>
          <div
            className="modal modal-sm"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>{viewProposal.reference_code}</h2>
              <button className="modal-close" onClick={() => setViewProposal(null)}>&times;</button>
            </div>

            <div className="page-tabs">
              <button
                type="button"
                className={`page-tab ${viewProposalTab === 'overview' ? 'active' : ''}`}
                onClick={() => setViewProposalTab('overview')}
              >
                Overview
              </button>
              <button
                type="button"
                className={`page-tab ${viewProposalTab === 'activity' ? 'active' : ''}`}
                onClick={() => setViewProposalTab('activity')}
              >
                Activity {proposalEvents.length > 0 && <span className="tab-badge">{proposalEvents.filter(e => e.event_type !== 'time_on_page').length}</span>}
              </button>
            </div>

            <div className="modal-body">
              {viewProposalTab === 'activity' && (
                <ProposalActivityTab proposalId={viewProposal.id} supabase={supabase} />
              )}
              {viewProposalTab === 'overview' && (
                <>
              {/* Status */}
              <div className="view-section pos-proposal-view-status">
                {(() => {
                  const cfg = (STATUS_CONFIG[viewProposal.status] || STATUS_CONFIG.draft)!;
                  return <StatusBadge bg={cfg.bg} color={cfg.color}>{cfg.icon} {cfg.label}</StatusBadge>;
                })()}
                {viewProposal.is_locked && <span className="pos-proposal-locked-tag">🔒 Locked</span>}
              </div>

              {/* Property */}
              <div className="view-section">
                <h3 className="section-title">Property</h3>
                <div className="fw-500">{viewProposal.property_name}</div>
                <div className="text-sm text-muted">
                  {[viewProposal.property_type, viewProposal.property_city, viewProposal.property_province].filter(Boolean).join(' · ')}
                </div>
              </div>

              {/* Contact */}
              <div className="view-section">
                <h3 className="section-title">Contact</h3>
                <div>{viewProposal.contact_name}</div>
                {viewProposal.contact_email && <div className="text-sm text-muted">{viewProposal.contact_email}</div>}
                {viewProposal.contact_phone && <div className="text-sm text-muted">{viewProposal.contact_phone}</div>}
              </div>

              {/* Team */}
              <div className="view-section">
                <h3 className="section-title">Team</h3>
                <div className="detail-row"><span className="label">Created by</span><span className="value">{viewProposal.ambassador_name || <span className="text-muted">—</span>}</span></div>
                {viewProposal.sender_name && <div className="detail-row"><span className="label">Sender</span><span className="value">{viewProposal.sender_name}</span></div>}
                <div className="detail-row"><span className="label">POS Ambassador</span><span className="value">{viewProposal.assigned_ambassador_name || <span className="text-muted">No Ambassador</span>}</span></div>
                <div className="detail-row"><span className="label">Manager</span><span className="value">{viewProposal.assigned_manager_name || <span className="text-muted">—</span>}</span></div>
              </div>

              {/* Scope */}
              <div className="view-section">
                <h3 className="section-title">Scope</h3>
                {(() => {
                  const scope = ((viewProposal.proposal_data as { scope?: { rooms?: ScopeRoom[]; items?: ScopeItem[] } } | null | undefined)?.scope) || {};
                  const rooms: ScopeRoom[] = scope.rooms || [];
                  const hasRoomItems = rooms.length > 0 && rooms.some((r: ScopeRoom) => Array.isArray(r.items) && r.items.length > 0);

                  if (hasRoomItems) {
                    // New room+items shape
                    let totalSurfaces = 0;
                    return (
                      <div className="pos-proposal-detail-block">
                        {rooms.map((r: ScopeRoom, i: number) => {
                          const qty = r.quantity || 1;
                          const roomItems: ScopeItem[] = r.items || [];
                          const roomSurfaces = roomItems.reduce((s: number, it: ScopeItem) => s + (it.quantity || 0) * qty, 0);
                          totalSurfaces += roomSurfaces;
                          return (
                            <div key={i} className="pos-proposal-scope-room">
                              <div className="fw-500 pos-proposal-scope-room-name">
                                {r.name || 'Room'}{qty > 1 ? ` (×${qty})` : ''}
                              </div>
                              {roomItems.map((it: ScopeItem, j: number) => (
                                <div key={j} className="detail-row pos-proposal-scope-item">
                                  <span className="label">{it.item_name}</span>
                                  <span className="value">{it.quantity}{qty > 1 ? ` ×${qty}` : ''}</span>
                                </div>
                              ))}
                            </div>
                          );
                        })}
                        <div className="detail-row pos-proposal-detail-divider">
                          <span className="label"><strong>Total surfaces</strong></span>
                          <span className="value"><strong>{totalSurfaces}</strong></span>
                        </div>
                      </div>
                    );
                  } else if (scope.items) {
                    // Flat items
                    return (
                      <div className="pos-proposal-detail-block">
                        {scope.items.map((it: ScopeItem, i: number) => (
                          <div key={i} className="detail-row">
                            <span className="label">{it.item_name}</span>
                            <span className="value">{it.quantity}</span>
                          </div>
                        ))}
                        <div className="detail-row pos-proposal-detail-divider">
                          <span className="label"><strong>Total surfaces</strong></span>
                          <span className="value"><strong>{scope.items.reduce((s: number, it: ScopeItem) => s + it.quantity!, 0)}</strong></span>
                        </div>
                      </div>
                    );
                  } else if (rooms.length > 0) {
                    // Legacy rooms (beds_small/beds_large)
                    return (
                      <div className="pos-proposal-detail-block">
                        {rooms.map((r: ScopeRoom, i: number) => (
                          <div key={i} className="detail-row">
                            <span className="label">{r.name || 'Room'}{r.quantity! > 1 ? ` (×${r.quantity})` : ''}</span>
                            <span className="value">
                              {[
                                r.beds_small! > 0 && `${r.beds_small! * r.quantity!} sm`,
                                r.beds_large! > 0 && `${r.beds_large! * r.quantity!} lg`,
                              ].filter(Boolean).join(', ')} beds
                            </span>
                          </div>
                        ))}
                        <div className="detail-row pos-proposal-detail-divider">
                          <span className="label"><strong>Total</strong></span>
                          <span className="value"><strong>{viewProposal.beds_small + viewProposal.beds_large} beds</strong></span>
                        </div>
                      </div>
                    );
                  } else {
                    // Flat bed/topper counts (oldest)
                    return (
                      <div className="view-grid pos-proposal-view-grid-2">
                        {viewProposal.beds_small > 0 && <div className="detail-row"><span className="label">Small Beds</span><span className="value">{viewProposal.beds_small}</span></div>}
                        {viewProposal.beds_large > 0 && <div className="detail-row"><span className="label">Large Beds</span><span className="value">{viewProposal.beds_large}</span></div>}
                        {viewProposal.toppers_small > 0 && <div className="detail-row"><span className="label">Small Toppers</span><span className="value">{viewProposal.toppers_small}</span></div>}
                        {viewProposal.toppers_large > 0 && <div className="detail-row"><span className="label">Large Toppers</span><span className="value">{viewProposal.toppers_large}</span></div>}
                      </div>
                    );
                  }
                })()}
              </div>

              {/* Pricing */}
              <div className="view-section">
                <h3 className="section-title">Pricing</h3>
                <div className="detail-row"><span className="label">Tier</span><span className="value">{TIER_LABELS[viewProposal.selected_tier]}</span></div>
                <div className="detail-row"><span className="label">Visits/year</span><span className="value">{viewProposal.visits_per_year}</span></div>
                <div className="detail-row"><span className="label">Per visit</span><span className="value">{formatCurrency(viewProposal.total_per_visit)}</span></div>
                <div className="detail-row">
                  <span className="label">Annual Total</span>
                  <span className="value pos-proposal-annual-value">
                    {formatCurrency(viewProposal.annual_total)}
                  </span>
                </div>
              </div>

              {/* Timeline */}
              <div className="view-section">
                <h3 className="section-title">Timeline</h3>
                <div className="text-sm">
                  <div className="detail-row"><span className="label">Created</span><span className="value">{formatDate(viewProposal.created_at)}</span></div>
                  {viewProposal.sent_at && <div className="detail-row"><span className="label">Sent</span><span className="value">{formatDate(viewProposal.sent_at)}</span></div>}
                  {viewProposal.viewed_at && <div className="detail-row"><span className="label">First viewed</span><span className="value">{formatDate(viewProposal.viewed_at)}</span></div>}
                  {viewProposal.accepted_at && <div className="detail-row"><span className="label">Accepted</span><span className="value">{formatDate(viewProposal.accepted_at)}</span></div>}
                  {viewProposal.expires_at && <div className="detail-row"><span className="label">Expires</span><span className="value">{formatDate(viewProposal.expires_at)}</span></div>}
                </div>
              </div>

              {/* Internal Note */}
              {viewProposal.personal_note && (
                <div className="view-section">
                  <h3 className="section-title">Internal Note</h3>
                  <div className="pos-personal-note">
                    {viewProposal.personal_note}
                  </div>
                </div>
              )}

              {/* Activity / Analytics */}
              <div className="view-section">
                <h3 className="section-title">Activity</h3>
                {eventsLoading ? (
                  <div className="text-sm text-muted pos-proposal-activity-pad">Loading...</div>
                ) : proposalEvents.length === 0 ? (
                  <div className="text-sm text-muted pos-proposal-activity-pad">No activity yet</div>
                ) : (() => {
                  const views = proposalEvents.filter(e => e.event_type === 'view');
                  const scrolls = proposalEvents.filter(e => e.event_type === 'scroll');
                  const times = proposalEvents.filter(e => e.event_type === 'time_on_page');
                  const clicks = proposalEvents.filter(e => e.event_type === 'cta_click');
                  const lastView = views[0];
                  const maxTime = times.length > 0 ? Math.max(...times.map(t => t.event_data?.seconds || 0)) : 0;
                  const sections = [...new Set(scrolls.map(s => s.event_data?.section).filter(Boolean))];
                  const mobileViews = views.filter(v => v.event_data?.is_mobile).length;
                  const desktopViews = views.length - mobileViews;

                  const timeLabel = maxTime >= 180 ? '3min+' : maxTime >= 60 ? `${Math.floor(maxTime / 60)}min ${maxTime % 60}s` : maxTime > 0 ? `${maxTime}s` : null;

                  const timeAgo = (dateStr: string) => {
                    const diff = Date.now() - new Date(dateStr).getTime();
                    const mins = Math.floor(diff / 60000);
                    if (mins < 1) return 'just now';
                    if (mins < 60) return `${mins}m ago`;
                    const hrs = Math.floor(mins / 60);
                    if (hrs < 24) return `${hrs}h ago`;
                    const days = Math.floor(hrs / 24);
                    return `${days}d ago`;
                  };

                  return (
                    <>
                      {/* Summary */}
                      <div className="pos-proposal-activity-summary">
                        <strong>{views.length}</strong> view{views.length !== 1 ? 's' : ''}
                        {lastView && <span className="text-muted"> · last {timeAgo(lastView.created_at)}</span>}
                        {lastView && <span className="text-muted"> · {lastView.event_data?.is_mobile ? 'mobile' : 'desktop'}</span>}
                        {mobileViews > 0 && desktopViews > 0 && <span className="text-muted"> ({desktopViews} desktop, {mobileViews} mobile)</span>}
                        {timeLabel && <><br />Read time: <strong>{timeLabel}</strong></>}
                        {sections.length > 0 && <><br />Scrolled to: {sections.map(s => <span key={s} className="pos-proposal-activity-chip-info">{s}</span>)}</>}
                        {clicks.length > 0 && <><br />Clicked: {[...new Set(clicks.map(c => c.event_data?.target))].filter(Boolean).map(t => <span key={t} className="pos-proposal-activity-chip-success">{t}</span>)}</>}
                      </div>

                      {/* Timeline */}
                      <div className="pos-proposal-activity-list">
                        {proposalEvents.slice(0, 50).map((evt, i) => {
                          const icons: Record<string, string> = { view: '👁', scroll: '📜', time_on_page: '⏱', cta_click: '👆' };
                          const labels: Record<string, string> = {
                            view: `Viewed${evt.event_data?.is_mobile ? ' (mobile)' : ' (desktop)'}`,
                            scroll: `Scrolled to ${evt.event_data?.section || '?'}`,
                            time_on_page: `On page ${evt.event_data?.seconds || 0}s`,
                            cta_click: `Clicked ${evt.event_data?.target || '?'}`,
                          };
                          return (
                            <div key={i} className="pos-proposal-activity-row">
                              <span>{icons[evt.event_type] || '·'}</span>
                              <span className="pos-proposal-activity-row-label">{labels[evt.event_type] || evt.event_type}</span>
                              <span className="text-muted">{timeAgo(evt.created_at)}</span>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  );
                })()}
              </div>
                </>
              )}
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-primary"
                onClick={() => handleCopyLink(viewProposal.reference_code, viewProposal.access_key)}
              >
                Copy Link
              </button>
              {viewProposal.signed_pdf_url ? (
                <button
                  type="button"
                  className="btn btn-primary pos-proposal-success-btn"
                  onClick={() => void openSignedAgreement(viewProposal.signed_pdf_url!, 'Signed Agreement')}
                >
                  View Signed Agreement
                </button>
              ) : (
                <button
                  className="btn btn-primary"
                  onClick={() => handleCopyAgreementLink(viewProposal.reference_code, viewProposal.access_key)}
                  disabled={viewProposal.status === 'accepted'}
                >
                  Agreement Link
                </button>
              )}
              <button className="btn btn-secondary" onClick={() => setViewProposal(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ====== ONBOARD CONFIRMATION MODAL ====== */}
      {onboardPreview && (
        <div className="modal-overlay" onClick={() => setOnboardPreview(null)}>
          <div className="modal modal-md" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Onboard: {onboardPreview.group.group_name}</h2>
              <button className="modal-close" onClick={() => setOnboardPreview(null)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="pos-proposal-onboard-intro">
                This will create operational property records from the proposal. Review and assign managers before confirming.
              </div>

              <div className="pos-proposal-table-wrap">
                <table className="pos-proposal-table">
                  <thead>
                    <tr className="pos-proposal-table-head-row">
                      <th className="pos-proposal-onboard-th">Property</th>
                      <th className="pos-proposal-onboard-th-center">Rooms</th>
                      <th className="pos-proposal-onboard-th">Customer</th>
                      <th className="pos-proposal-onboard-th">Assign User</th>
                      <th className="pos-proposal-onboard-th-center">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {onboardPreview.properties.map((p: OnboardPreviewProperty) => (
                      <tr key={p.proposal_id} className="pos-proposal-tr">
                        <td className="pos-proposal-onboard-td">
                          <div className="fw-500">{p.property_name}</div>
                          <div className="text-sm text-muted">{[p.property_type, p.city].filter(Boolean).join(' · ')}</div>
                        </td>
                        <td className="pos-proposal-onboard-td-center">{p.rooms.reduce((s: number, r: { quantity: number }) => s + r.quantity, 0)}</td>
                        <td className="pos-proposal-onboard-td">
                          {p.already_onboarded ? (
                            <span className="text-sm text-muted">—</span>
                          ) : (() => {
                            const choice = onboardCustomers[p.proposal_id];
                            const suggestions = (p.customer_suggestions ?? []) as Array<{ id: string; customer_name: string; company_name?: string | null; email?: string | null; match_source: string }>;
                            const value = !choice ? '' : choice.mode === 'create' ? '__create__' : choice.customer_id;
                            return (
                              <SearchSelect
                                width="inline"
                                options={[
                                  { value: '', label: 'Select customer...' },
                                  ...suggestions.map(s => ({ value: s.id, label: `${s.customer_name}${s.company_name && s.company_name !== s.customer_name ? ` · ${s.company_name}` : ''} (match: ${s.match_source})` })),
                                  { value: '__create__', label: '+ Create new customer from proposal' },
                                ]}
                                value={value}
                                onChange={v => {
                                  setOnboardCustomers(prev => {
                                    const next = { ...prev };
                                    if (!v) { delete next[p.proposal_id]; return next; }
                                    if (v === '__create__') next[p.proposal_id] = { mode: 'create' };
                                    else next[p.proposal_id] = { mode: 'existing', customer_id: v };
                                    return next;
                                  });
                                }}
                              />
                            );
                          })()}
                        </td>
                        <td className="pos-proposal-onboard-td">
                          {p.already_onboarded ? (
                            <span className="text-sm text-muted">Already onboarded</span>
                          ) : (
                            <SearchSelect
                              placeholder="Select manager..."
                              options={onboardPreview.managers.map((m: OnboardManager) => ({ value: m.id, label: `${m.first_name} ${m.last_name}` }))}
                              value={onboardManagers[p.proposal_id] || null}
                              onChange={v => setOnboardManagers(prev => ({ ...prev, [p.proposal_id]: v }))}
                            />
                          )}
                        </td>
                        <td className="pos-proposal-onboard-td-center">
                          {p.already_onboarded ? (
                            <span className="pos-proposal-onboard-pill-done">Done</span>
                          ) : (
                            <span className="pos-proposal-onboard-pill-new">New</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="pos-proposal-onboard-summary">
                <div className="detail-row"><span className="label">Group</span><span className="value">{onboardPreview.group.group_name}</span></div>
                <div className="detail-row"><span className="label">Tier</span><span className="value">{TIER_LABELS[onboardPreview.group.tier]}</span></div>
                <div className="detail-row"><span className="label">Annual Total</span><span className="value">{formatCurrency(onboardPreview.group.annual_total)}</span></div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setOnboardPreview(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleOnboardConfirm} disabled={onboardLoading}>
                {onboardLoading ? 'Onboarding...' : 'Confirm & Create Properties'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Individual Property Onboard Modal */}
      {propertyOnboardPreview && (
        <div className="modal-overlay" onClick={() => { setPropertyOnboardPreview(null); setPropertyOnboardCustomer(null); }}>
          <div className="modal modal-md" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Onboard: {propertyOnboardPreview.proposal.property_name}</h2>
              <button className="modal-close" onClick={() => { setPropertyOnboardPreview(null); setPropertyOnboardCustomer(null); }}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="pos-proposal-onboard-intro">
                This will create an operational property record, customer, and rooms from the proposal data.
              </div>

              <div className="pos-proposal-onboard-card">
                <div className="detail-row"><span className="label">Property</span><span className="value fw-500">{propertyOnboardPreview.proposal.property_name}</span></div>
                <div className="detail-row"><span className="label">Type</span><span className="value">{propertyOnboardPreview.proposal.property_type || '-'}</span></div>
                <div className="detail-row"><span className="label">Location</span><span className="value">{[propertyOnboardPreview.proposal.city, propertyOnboardPreview.proposal.province].filter(Boolean).join(', ') || '-'}</span></div>
                <div className="detail-row"><span className="label">Contact</span><span className="value">{propertyOnboardPreview.proposal.contact_name || '-'}</span></div>
                <div className="detail-row"><span className="label">Tier</span><span className="value">{TIER_LABELS[propertyOnboardPreview.proposal.tier] || propertyOnboardPreview.proposal.tier}</span></div>
                <div className="detail-row"><span className="label">Annual Total</span><span className="value">{formatCurrency(propertyOnboardPreview.proposal.annual_total)}</span></div>
                <div className="detail-row"><span className="label">Rooms</span><span className="value">{propertyOnboardPreview.proposal.rooms?.reduce((s: number, r: { quantity: number }) => s + r.quantity, 0) || 0}</span></div>
              </div>

              <div className="form-group">
                <label className="form-label">Customer</label>
                {(() => {
                  const suggestions = (propertyOnboardPreview.proposal.customer_suggestions ?? []) as Array<{ id: string; customer_name: string; company_name?: string | null; email?: string | null; match_source: string }>;
                  const suggIds = new Set(suggestions.map(s => s.id));
                  const value = !propertyOnboardCustomer
                    ? ''
                    : propertyOnboardCustomer.mode === 'create'
                      ? '__create__'
                      : propertyOnboardCustomer.customer_id;
                  return (
                    <SearchSelect
                      maxVisible={50}
                      options={[
                        { value: '', label: 'Select customer...' },
                        ...suggestions.map(s => ({ value: s.id, label: `${s.customer_name}${s.company_name && s.company_name !== s.customer_name ? ` · ${s.company_name}` : ''} (suggested)`, meta: s.email ?? '' })),
                        { value: '__create__', label: '+ Create new customer from proposal' },
                        ...onboardCustomerList
                          .filter(c => !suggIds.has(c.id))
                          .map(c => ({ value: c.id, label: `${c.customer_name}${c.company_name && c.company_name !== c.customer_name ? ` · ${c.company_name}` : ''}`, meta: c.email ?? '' })),
                      ]}
                      value={value}
                      onChange={v => {
                        if (!v) setPropertyOnboardCustomer(null);
                        else if (v === '__create__') setPropertyOnboardCustomer({ mode: 'create' });
                        else setPropertyOnboardCustomer({ mode: 'existing', customer_id: v });
                      }}
                    />
                  );
                })()}
              </div>

              <div className="form-group">
                <label className="form-label">Assign Manager</label>
                <select
                  className="form-input"
                  value={propertyOnboardManagerId}
                  onChange={e => setPropertyOnboardManagerId(e.target.value)}
                >
                  <option value="">Select manager...</option>
                  {propertyOnboardPreview.managers?.map((m: OnboardManager) => (
                    <option key={m.id} value={m.id}>{m.first_name} {m.last_name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => { setPropertyOnboardPreview(null); setPropertyOnboardCustomer(null); }}>Cancel</button>
              <button className="btn btn-primary" onClick={handlePropertyOnboardConfirm} disabled={onboardLoading || !propertyOnboardManagerId || !propertyOnboardCustomer}>
                {onboardLoading ? 'Onboarding...' : 'Confirm & Create Property'}
              </button>
            </div>
          </div>
        </div>
      )}

      {pdfPreview && (
        <PdfModal url={pdfPreview.url} title={pdfPreview.title} onClose={() => setPdfPreview(null)} />
      )}
    </div>
  );
}

export default POSProposals;
